// The shell reducer both games ran as two copies (docs/design/shared-shell.md §4.2, moved here in
// §5 C2): the home screen, the waiting rooms, the session (who plays, from which device), the
// resume offer, the Play tab's submenu and the leave flow, over intents, returning the next App
// with a list of effects as data. Gin's cases as they stood after C1 (`App = { shell, table }`),
// with everything the two copies disagreed on injected through a `ShellConfig` (§4.3): the game's
// id, names, tabs, copy, option codec, engine, frame builders, storage and the table hooks
// (`reset`, `rendered`, `refuse`, the pass-and-play `viewer`/`revealer`). The record is generic
// over one bag of the game's types (`ShellTypes`) rather than a dozen parameters, so a signature
// reads `ShellState<G>`; every literal this file writes into a generic field (`'play'`,
// `'online'`, `'homeScreen'`, `'longPress'`, `'tap'`) belongs to the shared half of that field's
// union, and the game's own half (`G['Tab']`, `G['Mode']`, …) is added by the config. Nothing
// here names a game (eslint.config.js: shared code never imports one), touches the store (the
// `Store` type is the game's, through `G['Store']`, because this zone reaches only web/shared/lib
// and the DOM edge); a session's status copy (`copyOf`: `opening`, `connecting`, `handoff`) is
// web/shared/lib/shellDefaults.ts's, which web/shared/net speaks too, unless the game's config
// has words of its own. The effect
// runner is web/shared/ui/shellEffects.ts, beside this file, because it calls the adapters
// (statements the pure profile refuses). A game's `ui/state.ts` keeps its table slice and hooks,
// spells `reduce = isShellIntent ? reduceShell : tableIntent`, and re-exports what its tests
// import, so both games' state.test.ts run unchanged but for the `opts` rename C1 agreed.
//
// Two legacy traits kept on purpose (the gin reducer's header): the shell runs `rendered`'s state
// effects wherever the legacy called `render()`, and a leave closes the network before the state
// is reset, so the session's own close still raises the "disconnected" toast the legacy raised.
import {
  DEFAULT_GUEST_NAME,
  EPHEMERAL_TAG,
  NAME_MAX,
  guestNameFor,
  type EphemeralFrame,
  type GuestFrame,
  type HostFrame,
} from '../lib/protocol.ts';
import {
  arrayOf,
  boolean,
  formatError,
  integer,
  nullable,
  object,
  string,
  type Decoder,
} from '../lib/json.ts';
import { handoffLabel } from '../lib/name.ts';
import { appendCapped, outcomeFor, type RecentGame } from '../lib/recentGames.ts';
import { ok, type Result } from '../lib/result.ts';
import type { Rng } from '../lib/rng.ts';
import { randomCode, sanitiseCode, validateCode, type Game } from '../lib/roomCode.ts';
import {
  DEFAULT_HOME_TAB,
  DEFAULT_NAME,
  DEFAULT_PLAY_MODE,
  HOME_TABS,
  OPENING_MSG,
  connectingMsg,
  handoffMsg,
  type HomeTab,
  type PlayMode,
} from '../lib/shellDefaults.ts';
import { DEFAULT_SOUND_FONT, type SoundFontName } from '../lib/sound/fonts.ts';
import type { Phrase } from '../lib/sound/phrase.ts';
import type { RulesSlot } from './glossary.ts';

// ---- the game's types, in one bag --------------------------------------------------------------

/** The two seats every shell flow names: the host and its guest, pass-and-play's two players. A game with more adds them through its bag (`ShellTypes.Seat`) and the flows type on `SeatOf<G>`. */
export type Seat = 0 | 1;
export type Role = 'host' | 'guest' | 'local';
/** The stored modes (web/shared/lib/shellDefaults.ts `PLAY_MODES`); a game may show more (`G['Mode']`). */
export { DEFAULT_NAME, type PlayMode };
/** The stored flip setting (prefs.ts `FLIP_STATES`), which the shell holds as the boolean `flipForFar`. */
export type FlipState = 'on' | 'off';
/** A seat as the engines take it: gin's `PlayerInfo`, backgammon's `Player`. */
export type Player = Readonly<{ id: string; name: string }>;

/**
 * What a game plugs into the shell's types. Each member is the game's whole union where the shell
 * adds its own literals (`Tab`, `Mode`, `Screen`, `Timer`, `Cue`: the shared literals are unioned
 * in by `Tab<G>` and friends, so a game lists its full set and nothing is spelled twice), or the
 * game's own slice where the shell has none (`Table`, `Cues`, `Home`, `Intent`, `Effect`).
 */
export type ShellTypes = Readonly<{
  /** A room's terms: gin `{ target }`, backgammon `{ matchLength, variant }`. The host save's, the welcome frame's and the resume offer's own fields, in the literal's order. */
  Opts: object;
  /** The raw option values `host/click` and `local/click` carry off the inputs, parsed by `cfg.opts.parse`. */
  Raw: object;
  State: unknown;
  View: unknown;
  Action: unknown;
  /**
   * The seats beyond the shell's two, for a game whose table seats three or four (`2 | 3`); a
   * two-seat game leaves it out, and every flow types on `0 | 1` as before (`SeatOf<G>`).
   */
  Seat?: number;
  /** The table slice; the shell reads and writes only its pass-and-play `curtain` (agreed in C1), a seat of the game's (`SeatOf<G>`). */
  Table: Readonly<{ curtain: number | null }>;
  Tab: string;
  Mode: string;
  Screen: string;
  Timer: string;
  Cue: string;
  /** The cue machine's memory (`CueMemory`, or a record extending it: gin adds `turnKey`), shell state because it keys on the view. */
  Cues: unknown;
  /** Resume offers beyond the three save roles: gin's Score Counter session; `never` elsewhere. */
  Resume: Readonly<{ kind: string }>;
  /** What `initHome` reads beyond the shell's own keys: gin's sort, card back and scorer; backgammon's options and curtain mode. */
  Home: object;
  /** The game's own intents and effects, the table half of its unions. */
  Intent: Readonly<{ type: string }>;
  Effect: Readonly<{ type: string }>;
  Store: unknown;
  /**
   * The game's ephemeral frame (web/shared/lib/protocol.ts `EPHEMERAL_TAG`): what either side
   * sends over the lane and `cfg.table.ephemeral` receives (briscola's live intent mirror,
   * docs/design/briscola-battle.md §4.5); a game with none leaves it out and its frame unions are
   * the seven frames as before.
   */
  Ephemeral?: EphemeralFrame;
}>;

/**
 * The bag members most games leave at the shell's own (dry-review-2026-10.md §2.6: nine of Flip
 * 7's seventeen were these): the three tabs, the two stored modes, no screen, timer or effect of
 * the game's, the plain cue memory, no resume offer beyond the three roles, nothing read at home.
 */
export type ShellTypeDefaults = Readonly<{
  Tab: HomeTab;
  Mode: PlayMode;
  Screen: never;
  Timer: never;
  Cues: CueMemory;
  Resume: never;
  Home: object;
  Effect: never;
}>;
/** What every game names for itself: its engine's four types, its table slice, its cues and intents, its store, and (past two seats) its `Seat`. */
type OwnTypes = Pick<
  ShellTypes,
  'Opts' | 'Raw' | 'State' | 'View' | 'Action' | 'Table' | 'Cue' | 'Intent' | 'Store' | 'Seat'
>;
/**
 * A game's bag from what it names: `ShellTypeDefaults` where `Own` is silent, `Own` where it
 * speaks (gin's tabs, screens, timer, cue state, resume and home; fidice's modes). `Store` is the
 * game's because this zone may not name web/shared/edge/storage.ts's.
 */
export type GameTypes<Own extends OwnTypes & Partial<ShellTypes>> = Readonly<
  Omit<ShellTypeDefaults, keyof Own> & Own
>;

/** The game's seats beyond the shell's own two: what its bag's `Seat` names, `never` when it names none. */
type ExtraSeats<G extends ShellTypes> =
  G extends Readonly<{ Seat: infer S extends number }> ? S : never;
/** A seat at the game's table: the shell's two and the game's own; `0 | 1` for the two-seat games. */
export type SeatOf<G extends ShellTypes> = Seat | ExtraSeats<G>;
/**
 * What `engine.create` deals to (n-seat-sessions.md §7 `engine.create(players[])`): the pair for a
 * two-seat game, whose bag names no seat past `0 | 1` (so gin's and backgammon's engines keep their
 * tuple, type for type), and every seat in order for a game with more (`ShellTypes.Seat`).
 */
export type PlayersOf<G extends ShellTypes> = [ExtraSeats<G>] extends [never]
  ? Readonly<[Player, Player]>
  : ReadonlyArray<Player>;
/** The three tabs every shell page carries (`HOME_TABS`) and the game's own (gin's `score`, fidice's `ladder`). */
export type Tab<G extends ShellTypes> = 'play' | 'rules' | 'about' | G['Tab'];
export type Mode<G extends ShellTypes> = PlayMode | G['Mode'];
/**
 * The five screens every shell page carries (web/shared/markup/shell.ts composes them;
 * shellPaint.ts `paintShellChrome` toggles them): a game with more spreads this list first
 * (fidice's `configScreen`, gin's scorer pair) and names only the extra in `ShellTypes.Screen`.
 */
export const SHELL_SCREENS = [
  'homeScreen',
  'hostWaitScreen',
  'guestWaitScreen',
  'tableScreen',
  'endgameScreen',
] as const;
export type ShellScreen = (typeof SHELL_SCREENS)[number];
/** The five screens every shell page carries, and the game's own. */
export type ScreenId<G extends ShellTypes> = ShellScreen | G['Screen'];
export type TimerId<G extends ShellTypes> = 'longPress' | G['Timer'];
export type Cue<G extends ShellTypes> = 'tap' | 'yourTurn' | G['Cue'];
/** The game's ephemeral frame, `never` when its bag names none. */
export type EphemeralOf<G extends ShellTypes> =
  G extends Readonly<{ Ephemeral: infer E extends EphemeralFrame }> ? E : never;
/** What arrives from, and goes to, a host: its five frames and the ephemeral one (either side sends that). */
export type HostFrameOf<G extends ShellTypes> = HostFrame<G['View'], G['Opts']> | EphemeralOf<G>;
/** What arrives from, and goes to, a guest: its two frames and the ephemeral one. */
export type GuestFrameOf<G extends ShellTypes> = GuestFrame<G['Action']> | EphemeralOf<G>;

/** `#hostWaitStatus` / `#guestWaitStatus`: the text and whether it still pulses. */
export type WaitStatus = Readonly<{ text: string; pulse: boolean }>;

/**
 * A guest seat of an open room (docs/design/n-seat-sessions.md §7): who holds it (null while it
 * is empty; kept while its channel is down mid-game, for the rejoin) and whether its channel is
 * open. Seat `s` of the room is `seats[s - 1]`; the host is seat 0 and has none.
 */
export type SeatState = Readonly<{ name: string | null; connected: boolean }>;
export const EMPTY_SEAT: SeatState = { name: null, connected: false };

// ---- the save, the resume offer and the home snapshot -------------------------------------------

/** `persist()` writes one of three shapes by role, the shapes web/shared/edge/prefs.ts `shellSave` reads and writes; the host's own fields sit between `myName` and `game`. */
export type LocalSave<G extends ShellTypes> = Readonly<{ role: 'local'; game: G['State'] }>;
export type HostSave<G extends ShellTypes> = Readonly<{
  role: 'host';
  code: string;
  myName: string;
}> &
  G['Opts'] &
  Readonly<{
    game: G['State'] | null;
    oppName: string | null;
    /** Every guest seat's name in seat order (`seats`), written only for a room of more than two seats, so a two-seat save keeps the legacy literal; a resume reseats each by name (n-seat-sessions.md D6). */
    seatNames?: ReadonlyArray<string | null>;
    /** Written only when true (the shell's `handoff`), so every other host save keeps the legacy literal. */
    handoff?: true;
    /** When the room was (re)opened, written only while it waits for its first guest (`game` null; docs/design/lobby-resume.md D1). */
    at?: number;
  }>;
export type GuestSave = Readonly<{ role: 'guest'; code: string; myName: string }>;
export type Save<G extends ShellTypes> = LocalSave<G> | HostSave<G> | GuestSave;

/** What the home screen's resume box offers (`initHome`), one per save role, plus the game's own (`G['Resume']`). */
export type HostResume<G extends ShellTypes> = Readonly<{
  kind: 'host';
  code: string;
  myName: string;
}> &
  G['Opts'] &
  Readonly<{
    /** The game in play, or null for a room still waiting for its first guest (lobby-resume.md D3). */
    game: G['State'] | null;
    oppName: string | null;
    /** The save's seat names, when it carries them (a room of more than two seats): the room resumes with each seat named and disconnected. */
    seatNames?: ReadonlyArray<string | null>;
    /** The save's `handoff` mark: the offer reads as the handoff, and the room resumes as one. */
    handoff: boolean;
    /** The save's `at`: when a waiting room was (re)opened, or null (a game in play, or a save from before the stamp). */
    at: number | null;
  }>;
export type ShellResume<G extends ShellTypes> =
  | Readonly<{ kind: 'local'; game: G['State'] }>
  | HostResume<G>
  | Readonly<{ kind: 'guest'; code: string; myName: string }>;
export type Resume<G extends ShellTypes> = ShellResume<G> | G['Resume'];

/** What the legacy `initHome` read from storage, in one snapshot (`readHome`): the shell's keys and the game's (`G['Home']`). */
export type HomeSnapshot<G extends ShellTypes> = Readonly<{
  name: string | null;
  /** The pass-and-play second name: each page's own key, so a legacy session has none. */
  p2Name: string | null;
  homeTab: Tab<G>;
  playMode: PlayMode;
  /** A bad value read as the default (main.ts logs it). */
  soundFont: SoundFontName;
  /** The far seat's flip (`flipForFar`): the game's `flipTable` key, off when missing or unreadable. */
  flipTable: boolean;
  save: Save<G> | null;
  /** The finished games this device remembers, newest first (web/shared/lib/recentGames.ts). */
  recentGames: ReadonlyArray<RecentGame>;
  /** The room's terms as remembered (`cfg.prefs.opts`); null for a game that forgets them (gin, hive), whose shell keeps its current ones. */
  opts: G['Opts'] | null;
  /** The third seat's name on, as remembered (`cfg.prefs.seatNames`, index 0 the third seat, null where nothing is stored); null or absent for a game that keeps none, whose shell keeps its current ones. */
  seatNames?: ReadonlyArray<string | null> | null;
}> &
  G['Home'];

// ---- the state ---------------------------------------------------------------------------------

/**
 * Everything but the table's own state: the legacy `app` object field for field (role, code,
 * names, the engine `State` for the host and pass-and-play, my `View` for every role), then what
 * the legacy kept in the DOM or in closures (the screen shown, the two waiting statuses, the
 * netAttempt ticket, the cue machine's memory, the sheets and the Play tab's submenu).
 */
export type ShellState<G extends ShellTypes> = Readonly<{
  role: Role | null;
  code: string | null;
  myName: string;
  /** The room's terms, where gin had `target` and backgammon `matchLength`/`variant` (agreed in C1). */
  opts: G['Opts'];
  /** The engine state: host and pass-and-play only. */
  game: G['State'] | null;
  /** My view: every role (the guest's arrives in `state` frames). */
  view: G['View'] | null;
  oppName: string | null;
  oppConnected: boolean;
  /**
   * The room's guest seats 1..N−1 in order (`seats[s − 1]`, n-seat-sessions.md §7): one per
   * channel the host session holds, `capacity − 1` of them from `startHost` on (one for a two-seat
   * game), what a guest's welcome or lobby frame carries beside the options in an N-seat game
   * (`seats` and `you`, D3, read by `roomSeatingOf`), and [] at home and in pass-and-play. As
   * host, `oppName`/`oppConnected` are
   * `seats[0]`'s: every shell write sets both (`withSeats`), and a two-seat game's flows keep
   * reading the two legacy fields (`seatsOf`), so its reducer, painters and pins are what they
   * were; as guest the two name the host.
   */
  seats: ReadonlyArray<SeatState>;
  /** My seat: 0 as host and in pass-and-play; as guest the `you` of the last welcome or lobby frame, 1 when the frame carries none. */
  mySeat: SeatOf<G>;
  /**
   * As guest, what the host calls my seat once it has answered: at a welcome or lobby frame with
   * no seating (a room of exactly two) the host's rule mirrored (`guestNameFor(myName, hostName)`:
   * exactly its `guestNameAmong` against its own name, the one other name at the table), at a
   * lobby with a seating my own row (`seats[you − 1]`, n-seat-sessions.md D3; a welcome's row is
   * not mine yet, `guestFrame`), then my seat's name off every `state` frame's view
   * (`cfg.result.seatName`, else `playersOf` at my seat: authoritative). Null until then, and in
   * every other role. `myName` stays what was typed: it is the rejoin key and what the save keeps.
   */
  seatedName: string | null;
  /** Pass-and-play: every player's name in seat order (`cfg.result.playersOf` of seat 0's view); [] otherwise. */
  localNames: ReadonlyArray<string>;
  /** Pass-and-play: the seats played from this device, every seat in v1 (the seam for a mixed table); [] otherwise. */
  localSeats: ReadonlyArray<SeatOf<G>>;
  /**
   * The online input has been typed into, or a remembered name filled it (`home/init`). Nothing
   * reads it since 2026-09-28 (the join takes the box's text as it is); kept for the moment as
   * state the binder still reports, its removal a follow-up.
   */
  nameTouched: boolean;
  /** Pass-and-play: the seat that lifted the curtain this turn. */
  revealed: SeatOf<G> | null;
  homeTab: Tab<G>;
  playMode: Mode<G>;
  /** The first player's name as last read from the name key or typed into any of its inputs. */
  p1Name: string;
  /** The second player's name as last read from its key or typed. */
  p2Name: string;
  /**
   * The pass-and-play names past the shell's two as last read from their keys or typed, index 0
   * the third seat, one entry per seat up to `cfg.seats.max` ([] for a two-seat game); null where
   * nothing was, '' for a seat the player cleared (the paint keeps it empty where null would refill
   * the default under the caret, web/shared/ui/seatNames.ts). A preference like `p1Name`: no table
   * reset touches it, and `local/click` seats them where its click carries no names.
   */
  seatNames: ReadonlyArray<string | null>;
  screen: ScreenId<G>;
  /**
   * The device is a phone held upright right now (web/shared/edge/media.ts `PORTRAIT_PHONE`, from
   * the boot's matchMedia watcher as `viewport/portrait`, at boot and on every turn of the phone;
   * only a game whose `cfg.orientation` is `'landscape'` is watched): a fact about the device, so
   * no reset touches it. What `gateOpen` reads.
   */
  portraitPhone: boolean;
  /** The mirror: a phone held sideways (`LANDSCAPE_PHONE`, `viewport/landscape`); a fact about the device too. */
  landscapePhone: boolean;
  /**
   * `#turnGateKeepBtn` "Play upright": the turn gate stays down for this table, and the Android
   * lock (`lockSideways`) steps nothing more for it: the gate offered the lock and the upright
   * board as the player's choice (docs/design/backgammon-landscape.md §6 item 11). Dropped where
   * the shell resets the table for a new one (a pass-and-play start, the handoff, a leave: the
   * `cfg.table.reset` sites for `startLocal`, `handoff` and `leave`) and at a cancel (the room
   * goes, and a guest whose host was lost takes no stale choice home), kept where the same table
   * stays up (the host lost mid-match, `lost`: the same table does not ask twice).
   */
  gateDismissed: boolean;
  /**
   * The rotation hint (`ROTATION_HINT_MSG`: lock the phone's rotation, Android's Auto-rotate) was
   * toasted at this table. Once per table, so it goes where `gateDismissed` goes: dropped at the
   * same five sites, kept on `lost`. Set by `rotationHint`, never by the player.
   */
  rotationHintShown: boolean;
  /**
   * The Android lock is held (docs/design/backgammon-landscape.md §5C; the owner, 2026-09-25: "it
   * should lock the user into place to make it sideways. Only on mobile!"): the `orientationLock`
   * effect was stepped at a tap (`lockSideways`: fullscreen on the document, then
   * `screen.orientation.lock('landscape')`) and nothing has reported it gone. Set as the effect is
   * emitted, so the paint inside the same tap already knows; dropped by `fullscreen/lost` (the
   * boot's `fullscreenchange` with no fullscreen element: a back gesture, which unlocks too; or
   * the adapter's own report that the attempt failed) and at every leave and cancel, where the
   * lock is dropped with it. A fact about the page, not the table: no table reset touches it.
   * While held, the rotation hint (`rotationHint`) is silent and no tap steps the effect again;
   * once lost, the next tap re-enters, and nothing but a tap can (`requestFullscreen` needs
   * transient activation).
   */
  orientationLocked: boolean;
  /** The `netAttempt` ticket: bumped by every start, cancel and leave. */
  netAttempt: number;
  hostStatus: WaitStatus;
  guestStatus: WaitStatus;
  /** `#startGameBtn` shown (a guest is in the lobby). */
  startGameVisible: boolean;
  /**
   * The hosted game came from pass-and-play (`#handoffBtn`) and its remote seat has not joined
   * yet: the wait screen tells the player to send the invite, a guest that drops before its join
   * leaves the wait screen as it is, and cancelling the room gives the game back to pass-and-play.
   * Saved with the host save (`HostSave.handoff`), so a reload resumes the offer. Cleared by the
   * guest's join and by every leave and cancel.
   */
  handoff: boolean;
  /** The saved name, as `initHome` put it in the inputs. */
  savedName: string | null;
  resume: Resume<G> | null;
  /**
   * When this device opened or reopened its room (`startHost`, the clock), null otherwise: the
   * waiting-room save's `at` (lobby-resume.md D2), which decides whether a reload resumes the
   * lobby by itself (`resume/auto`) or offers it.
   */
  openedAt: number | null;
  /** `#rulesOverlay` open (the in-game rules sheet; the home tab is `homeTab`). */
  rulesOpen: boolean;
  /** `#historyOverlay` open (the game's log and the finished games this device remembers). */
  historyOpen: boolean;
  /**
   * The result sheet put away with "Look at the table" (shell-hoist.md row F: hive's
   * `resultSeen`, briscola's and gin's `resultDismissed`, backgammon's `resultOpen` inverted).
   * Set by `result/dismiss`, cleared by `result/open` and wherever the table is reset for a start,
   * a deal, the handoff, a leave or the host lost; a game whose result sheet lifts when its view
   * moves on (hive's rematch, briscola's and backgammon's next game) clears it in `rendered`.
   */
  resultDismissed: boolean;
  /**
   * A consequential event held with its state until the player's Continue (AGENT.md "Understand
   * what happened before proceeding"; the owner, 2026-10-02: "When you the player bust, you need
   * to confirm before proceeding"; shell-hoist.md row H): what to show, raised by the game's
   * `table.pause` adapter as a new view is painted (`painted`, once: a pause already up is kept),
   * painted by `paintShellSheets` into `#pauseOverlay` (the `pause.html` partial a page opts into),
   * cleared by `pause/continue` and wherever the table is reset for a start, a deal, the handoff, a
   * leave or the host lost (`SHEETS_AWAY`). While it is up no action moves (`actAll`), and Escape
   * is its Continue. Online it is this phone's own seat's; on one phone, any seat's.
   */
  pause: Pause | null;
  cues: G['Cues'];
  /** `#playSubmenu` held open by a long press on the Play tab (`force-open`). */
  submenuOpen: boolean;
  /** A long press just opened the submenu, so the click that follows must not switch tabs. */
  longPressed: boolean;
  /** `#codeInput` as last sanitised (the legacy `lastGoodCode`). */
  codeDraft: string;
  /** The font every cue plays in (docs/design/sound-fonts.md §6). */
  soundFont: SoundFontName;
  /**
   * "Phone flat between us: flip the board each turn" (the pass-and-play setting; the owner,
   * 2026-09-25: the pass-the-phone flow "should naturally follow as the phone will be held
   * sideways"; docs/design/backgammon-landscape.md §6 item 7): a phone lying flat between two
   * players is read upside down by the one across the table, so the whole page turns 180° for
   * seat 1's turns (`flipped`, painted by the boot as `data-flip` on the body). A preference,
   * not a table fact: read at `home/init` from the game's `flipTable` key, written by `flip/set`
   * through `writeFlip`, kept through every start, leave and cancel. Off by default.
   */
  flipForFar: boolean;
  /**
   * The finished games this device remembers, newest first, at most RECENT_GAMES_CAP: read at
   * `home/init`, the record of a game that just ended put first as its `recordGame` effect
   * appends the same record to storage (web/shared/lib/recentGames.ts).
   */
  recentGames: ReadonlyArray<RecentGame>;
  /**
   * The key (`cfg.result.keyOf` of its view) of the game whose result was recorded, so a re-sent
   * frame or a repaint of the same finished game records nothing; null until a game ends, and
   * again after a leave.
   */
  recorded: string | null;
}>;

/** What a pause shows: its title large ("Ari busts") and the line under it (the card and the points lost). */
export type Pause = Readonly<{ title: string; detail: string }>;

export type ShellApp<G extends ShellTypes> = Readonly<{ shell: ShellState<G>; table: G['Table'] }>;

// ---- intents -----------------------------------------------------------------------------------

/** The shell's intents: every home, waiting-room, session and leave handler and every network event. */
export type ShellIntent<G extends ShellTypes> =
  // ---- home ----
  | Readonly<{ type: 'home/init'; home: HomeSnapshot<G> }>
  | Readonly<{ type: 'name/typed'; value: string }>
  | Readonly<{ type: 'p1name/typed'; value: string }>
  | Readonly<{ type: 'p2name/typed'; value: string }>
  /** A keystroke in the input of seat `seat` (0-based, the third seat is 2; web/shared/ui/seatNames.ts): kept as typed, remembered trimmed. */
  | Readonly<{ type: 'seatName/typed'; seat: number; value: string }>
  /** `setHomeTab(tab, { persist })`: an unknown tab is `play`. */
  | Readonly<{ type: 'tab/set'; tab: string; persist?: boolean }>
  /**
   * A glossary link (docs/design/glossary-links.md) or a `#rule-<id>` deep link at boot: the Rules
   * tab on the home screen, the rules overlay anywhere else, then the rule scrolled to and flashed.
   */
  | Readonly<{ type: 'rules/show'; rule: string }>
  /** `setPlayMode(mode)`: decoded by `cfg.modes.parse` (`local`, else `online`; gin's `sandbox` while unlocked). */
  | Readonly<{ type: 'mode/set'; mode: string }>
  /** An option control changed (a seat-count stepper, a select): the raw values parsed by `cfg.opts.parse` onto the shell's terms, and remembered through `cfg.prefs.opts` when the game keeps them. */
  | Readonly<{ type: 'opts/set'; raw: G['Raw'] }>
  /** `#hostBtn`: the raw name and the raw option values (`G['Raw']`), parsed by `cfg.opts.parse`. */
  | (Readonly<{ type: 'host/click'; name: string }> & G['Raw'])
  /** `#joinBtn`: the raw input values. */
  | Readonly<{ type: 'join/click'; name: string; code: string }>
  /** `#localBtn`: the raw names (`names`: the third seat's input on, in seat order, where the page has them; absent, the shell's `seatNames`) and the raw option values. */
  | (Readonly<{ type: 'local/click'; p1: string; p2: string; names?: ReadonlyArray<string> }> &
      G['Raw'])
  /** `#resumeBtn`: whatever `shell.resume` offers. */
  | Readonly<{ type: 'resume/click' }>
  /**
   * The boot, after the invite link (the owner, 2026-09-25: "When the host of a lobby refreshes,
   * it shouldn't drop the lobby"): a waiting room this device opened within WAITING_RESUME_MS
   * resumes by itself; every other offer waits for its tap. Nothing while seated.
   */
  | Readonly<{ type: 'resume/auto' }>
  /** `#handoffBtn` (pass-and-play alone): the game goes on as a hosted room. */
  | Readonly<{ type: 'handoff/click' }>
  /** `#cancelHostBtn` / `#cancelGuestBtn`. */
  | Readonly<{ type: 'cancel' }>
  | Readonly<{ type: 'cancel/finish' }>
  /** The hook's `showScreen(id)`. */
  | Readonly<{ type: 'screen/show'; screen: ScreenId<G> }>
  /** `#tabPlayBtn` pointerdown: the long-press timer starts. */
  | Readonly<{ type: 'submenu/press' }>
  /** `#tabPlayBtn` pointerup/leave/cancel: the timer is cancelled. */
  | Readonly<{ type: 'submenu/release' }>
  /** The long-press timer fired. */
  | Readonly<{ type: 'submenu/longPress' }>
  /** `#tabPlayBtn` click: the Play tab, unless a long press just opened the submenu. */
  | Readonly<{ type: 'tab/playClick' }>
  /** A `#playSubmenu` button. */
  | Readonly<{ type: 'submenu/pick'; mode: string }>
  /** A click outside `#tabPlayWrap`. */
  | Readonly<{ type: 'submenu/dismiss' }>
  /** `#codeInput` input: the raw value and the InputEvent's type. */
  | Readonly<{ type: 'code/typed'; value: string; inputType: string }>
  /**
   * `?join=<code>` at boot (an invite link): the code into `#codeInput`, the Play tab, online
   * mode, then the join `#joinBtn` would have dispatched, under the name the input shows (the
   * owner, 2026-09-25: "it shouldn't make you THEN click 'sit down'"). Nothing while seated.
   */
  | Readonly<{ type: 'join/link'; code: string }>
  /**
   * `#guestRenameBtn`, or Enter in `#guestNameInput` (the guest wait screen's name card; the
   * owner, 2026-09-28: the client defines its own name): the raw box text. A connected guest
   * re-sends its join under the new name, which the host takes as a re-seat (`hostFrame` `join`,
   * G5); ignored in every other role and while the host is not connected.
   */
  | Readonly<{ type: 'name/rename'; name: string }>
  /** `#soundBtn`. */
  | Readonly<{ type: 'sound/toggle' }>
  // ---- the sheets over the table (shell-hoist.md row F) ----
  /** `#rulesBtnGame`, and a game's menu row for the rules (`shellButtons`). */
  | Readonly<{ type: 'rules/open' }>
  /** `#closeRulesBtn`, the rules sheet's backdrop. */
  | Readonly<{ type: 'rules/close' }>
  /** `#historyBtn`, a game's menu row or endgame button for the history (`shellButtons`). */
  | Readonly<{ type: 'history/open' }>
  /** `#closeHistoryBtn`, the history sheet's backdrop. */
  | Readonly<{ type: 'history/close' }>
  /** The result sheet's "Look at the table" (`#rsPeekBtn`, hive's `#rsContinueBtn`): the sheet goes, the table shows. */
  | Readonly<{ type: 'result/dismiss' }>
  /** The chip that brings a put-away result back (`#resultChipBtn`). */
  | Readonly<{ type: 'result/open' }>
  /** The pause's `#continueBtn` (`pause.html`): the event was read, the table moves on (`ShellState.pause`). */
  | Readonly<{ type: 'pause/continue' }>
  /**
   * Play again once the game is over (`#rsAgainBtn`, flip7's `#rsNextBtn` at the game's end): a
   * fresh `engine.create` for the same seats and terms, dealt by the host or the phone (`again`);
   * a guest waits for the host's.
   */
  | Readonly<{ type: 'again/click' }>
  /**
   * Escape with no listed sheet open (shellPaint.ts `bindSheets`' fallback): the game's own thing
   * first (`cfg.table.escape`: a lifted card, a drag, a peek), then the history, then the rules;
   * nothing when nothing is up. A pause up is its Continue, before any of them.
   */
  | Readonly<{ type: 'escape' }>
  /** The hook's `soundFont(name)` (the console, for now): a valid font plays from now on and is remembered. */
  | Readonly<{ type: 'soundFont/set'; font: SoundFontName }>
  /** `#menuFlipToggle` (backgammon's menu sheet): the far seat's flip, `flipForFar`, remembered under the game's `flipTable` key. */
  | Readonly<{ type: 'flip/set'; on: boolean }>
  /** `#shareCodeBtn`. */
  | Readonly<{ type: 'share/click' }>
  // ---- net: host ----
  /** `startHost(resumeCode)`: null draws a fresh code. */
  | Readonly<{ type: 'host/start'; code: string | null }>
  | Readonly<{ type: 'host/status'; text: string; stopPulse: boolean }>
  /** A guest frame; `seat` is the channel it came in on, from the seated adapter (boot.ts `sessionEvents(deps, { seats: true })`), absent (seat 1) for a two-seat game. */
  | Readonly<{ type: 'host/frame'; frame: GuestFrameOf<G>; seat?: SeatOf<G> }>
  | Readonly<{ type: 'host/guestGone'; iceFailed: string | null; seat?: SeatOf<G> }>
  /** `#startGameBtn`. */
  | Readonly<{ type: 'host/deal' }>
  // ---- net: guest ----
  | Readonly<{ type: 'guest/start'; code: string }>
  | Readonly<{ type: 'guest/status'; text: string; stopPulse: boolean }>
  | Readonly<{ type: 'guest/connected' }>
  | Readonly<{ type: 'guest/frame'; frame: HostFrameOf<G> }>
  | Readonly<{ type: 'guest/lost' }>
  // ---- the table's shell half: the curtain, the leave flow, the paint (C1 left them on the table side; C2 moves them here through the hooks) ----
  /** `#curtainBtn`. */
  | Readonly<{ type: 'curtain/reveal' }>
  /**
   * The game's setup hook (e2e and stories; docs/design/dry-round-2.md F5): the pass-and-play
   * game's engine state is replaced by `state`, decoded by `cfg.engine.decodeState` so a hand-made
   * object is checked; refused with a toast outside a local game.
   */
  | Readonly<{ type: 'position/load'; state: unknown }>
  | Readonly<{ type: 'leave/request' }>
  | Readonly<{ type: 'leave/confirmed' }>
  | Readonly<{ type: 'leave/finish' }>
  /** The page became visible: the wake lock is taken again while in a game. */
  | Readonly<{ type: 'visible' }>
  // ---- playing sideways (docs/design/shared-shell.md "Playing sideways"; a game with `cfg.orientation`) ----
  /** The boot's matchMedia watcher on `PORTRAIT_PHONE` (web/shared/edge/media.ts): at boot and on every turn of the phone. */
  | Readonly<{ type: 'viewport/portrait'; portrait: boolean }>
  /** The boot's watcher on `LANDSCAPE_PHONE`; at the table, where the rotation hint is due (`rotationHint`). */
  | Readonly<{ type: 'viewport/landscape'; landscape: boolean }>
  /** `#turnGateKeepBtn` "Play upright": the turn gate stays down for this table. */
  | Readonly<{ type: 'gate/keep' }>
  /** `#turnGateGoBtn` "Go sideways" (shown where `Ctx.canLock`): the Android lock, `lockSideways`, from the gate's own tap. */
  | Readonly<{ type: 'gate/turn' }>
  /**
   * The boot's `fullscreenchange` with no fullscreen element (a back gesture left fullscreen, and
   * the spec unlocks the orientation with it), or the adapter's report that the attempt failed:
   * the lock is gone (`orientationLocked`), the next tap re-enters, and the rotation hint is due
   * again where it was silent.
   */
  | Readonly<{ type: 'fullscreen/lost' }>
  /** The hook's `render()`. */
  | Readonly<{ type: 'render' }>
  /** A session asked the app to persist. */
  | Readonly<{ type: 'persist' }>;

/**
 * The shell's half of a game's `Intent` union: 51 types, backgammon's 38 less its two option
 * selects (`variant/set`, `matchLength/set`, its own) plus the seven both games kept on the table
 * side after C1 (the curtain reveal, the leave flow, `visible`, `render`, `persist`), plus
 * `position/load`, backgammon's `sandbox/load` generalised (dry-round-2.md F5), plus
 * `resume/auto`, the boot's lobby resume (lobby-resume.md D4), plus the three of playing sideways
 * (`viewport/portrait`, `viewport/landscape`, `gate/keep`: backgammon's turn gate lifted here,
 * docs/design/backgammon-landscape.md §5D), plus the two of the Android lock (`gate/turn`,
 * `fullscreen/lost`, §5C), plus `flip/set`, the far seat's flip (§6 item 7), plus `name/rename`,
 * the guest wait screen's name card (the owner, 2026-09-28: the client defines its own name).
 */
export const SHELL_INTENT_TYPES = [
  'home/init',
  'name/typed',
  'p1name/typed',
  'p2name/typed',
  'seatName/typed',
  'tab/set',
  'rules/show',
  'mode/set',
  'opts/set',
  'host/click',
  'join/click',
  'local/click',
  'resume/click',
  'resume/auto',
  'handoff/click',
  'cancel',
  'cancel/finish',
  'screen/show',
  'submenu/press',
  'submenu/release',
  'submenu/longPress',
  'tab/playClick',
  'submenu/pick',
  'submenu/dismiss',
  'code/typed',
  'join/link',
  'name/rename',
  'sound/toggle',
  'rules/open',
  'rules/close',
  'history/open',
  'history/close',
  'result/dismiss',
  'result/open',
  'pause/continue',
  'again/click',
  'escape',
  'soundFont/set',
  'flip/set',
  'share/click',
  'host/start',
  'host/status',
  'host/frame',
  'host/guestGone',
  'host/deal',
  'guest/start',
  'guest/status',
  'guest/connected',
  'guest/frame',
  'guest/lost',
  'curtain/reveal',
  'position/load',
  'leave/request',
  'leave/confirmed',
  'leave/finish',
  'visible',
  'viewport/portrait',
  'viewport/landscape',
  'gate/keep',
  'gate/turn',
  'fullscreen/lost',
  'render',
  'persist',
] as const satisfies ReadonlyArray<ShellIntent<ShellTypes>['type']>;

/** A game's whole intent union: the shell's and its own. */
export type Intent<G extends ShellTypes> = ShellIntent<G> | G['Intent'];

const SHELL_INTENTS: ReadonlySet<string> = new Set(SHELL_INTENT_TYPES);
export const isShellIntent = <G extends ShellTypes>(intent: Intent<G>): intent is ShellIntent<G> =>
  SHELL_INTENTS.has(intent.type);

// ---- effects -----------------------------------------------------------------------------------

/** What the shell asks main.ts to do, as data; `runShellEffect` (shellEffects.ts) runs each against the adapters. */
export type ShellEffect<G extends ShellTypes> =
  | Readonly<{ type: 'persist' }>
  | Readonly<{ type: 'clearSave' }>
  /** A handed-off game given back to pass-and-play: the room was cancelled before anyone joined. */
  | Readonly<{ type: 'saveLocal'; game: G['State'] }>
  | Readonly<{ type: 'rememberName'; name: string }>
  | Readonly<{ type: 'rememberP2Name'; name: string }>
  /** Seat `seat`'s name (0-based, the third seat on) through `cfg.prefs.seatNames`; nothing for a seat the game keeps no key for. */
  | Readonly<{ type: 'rememberSeatName'; seat: number; name: string }>
  | Readonly<{ type: 'writeHomeTab'; tab: Tab<G> }>
  | Readonly<{ type: 'writePlayMode'; mode: PlayMode }>
  | Readonly<{ type: 'writeSoundFont'; font: SoundFontName }>
  /** The far seat's flip into the game's `flipTable` key (`on`/`off`). */
  | Readonly<{ type: 'writeFlip'; on: boolean }>
  /** The room's terms through `cfg.prefs.opts` (`opts/set`, and after `host/click`/`local/click`); emitted only for a game whose prefs hold them. */
  | Readonly<{ type: 'writeOpts'; opts: G['Opts'] }>
  /**
   * A game just ended on this device (the owner, 2026-09-25: "after a game is finished (either
   * online or pass-and-play) the datetime & score should be recorded, including the victor"):
   * the record, appended first to the game's `recentGames` pref; the shell state already holds it.
   */
  | Readonly<{ type: 'recordGame'; game: RecentGame }>
  /** Scroll `rule` into view inside the rules `slot` that is on screen and flash it (web/shared/edge/glossary.ts). */
  | Readonly<{ type: 'revealRule'; slot: RulesSlot; rule: string }>
  /** `ms` null is the default duration; `kind` `error` is the red one (`errorToast`), absent the plain one. */
  | Readonly<{ type: 'toast'; message: string; ms: number | null; kind?: ToastKind }>
  /** To the current session's channel, if open; `seat` names one of a host's channels (an N-seat game), absent every open one, which at capacity 2 is the one channel. */
  | Readonly<{ type: 'send'; frame: HostFrameOf<G> | GuestFrameOf<G>; seat?: SeatOf<G> }>
  | Readonly<{ type: 'fx'; cue: Cue<G> }>
  /**
   * Phrases the game's event binding chose (web/shared/ui/eventEffects.ts, sound-history.md
   * §3.5): played back to back in the App's font with one buzz, so a trick and the result it
   * ends the game with never sound under each other.
   */
  | Readonly<{ type: 'phrases'; phrases: ReadonlyArray<Phrase> }>
  | Readonly<{ type: 'wakeLock'; hold: boolean }>
  /**
   * The Android lock (docs/design/backgammon-landscape.md §5C): `hold` asks the adapter for
   * fullscreen on the document, then `screen.orientation.lock('landscape')`, inside the tap that
   * stepped it (`lockSideways`); false unlocks and leaves fullscreen (a leave, a cancel). Every
   * failure is silent at the adapter and reported back as `fullscreen/lost`.
   */
  | Readonly<{ type: 'orientationLock'; hold: boolean }>
  /** Open the room; `capacity` (seats, the host's included), `waiting` (the open status) and `names` (a resumed room's seat names, seeding the session's rejoin keys) ride only for an N-seat game (`HostOptions.capacity`/`waiting`/`names`), so a two-seat effect is the literal it was. */
  | Readonly<{
      type: 'startHost';
      code: string;
      attempt: number;
      resume: boolean;
      capacity?: number;
      waiting?: string;
      names?: ReadonlyArray<string | null>;
    }>
  | Readonly<{ type: 'startGuest'; code: string; attempt: number }>
  /** Close the current session's channel and destroy its Peer. */
  | Readonly<{ type: 'closeNet' }>
  /** `confirm(message)`: dispatch `then` when the player agrees. */
  | Readonly<{ type: 'confirm'; message: string; then: Intent<G> }>
  /** Dispatch `intent` next, after the effects before it ran. */
  | Readonly<{ type: 'then'; intent: Intent<G> }>
  /** Re-read storage and dispatch `home/init`. */
  | Readonly<{ type: 'initHome' }>
  /** `showScreen`'s `window.scrollTo(0, 0)`. */
  | Readonly<{ type: 'scrollTop' }>
  /** Arm a named timer that dispatches `then` after `ms`; arming again restarts it. */
  | Readonly<{ type: 'startTimer'; id: TimerId<G>; ms: number; then: Intent<G> }>
  | Readonly<{ type: 'cancelTimer'; id: TimerId<G> }>
  /** `fx.toggle()`. */
  | Readonly<{ type: 'toggleSound' }>
  /** The invite for `code` (its link) through the share sheet or the clipboard. */
  | Readonly<{ type: 'share'; code: string }>
  /**
   * The first player's name into every input that shows it (`initHome`, and after a keystroke).
   * `default` marks the shell's prefill (nothing remembered, `localNamesOf`): the page clears it on
   * the first tap, as the Score Counter clears its 0 (the owner, 2026-09-25: "when you click on a
   * pre-filled name for the first time it will clear it"); a remembered or typed name carries none.
   */
  | Readonly<{ type: 'fillName'; name: string; default?: true }>
  /** The second player's name into every input that shows it; `default` as `fillName`'s. */
  | Readonly<{ type: 'fillP2Name'; name: string; default?: true }>
  /** `#codeInput`'s value after sanitising. */
  | Readonly<{ type: 'setCode'; value: string }>;

export const SHELL_EFFECT_TYPES = [
  'persist',
  'clearSave',
  'saveLocal',
  'rememberName',
  'rememberP2Name',
  'rememberSeatName',
  'writeHomeTab',
  'writePlayMode',
  'writeSoundFont',
  'writeFlip',
  'writeOpts',
  'recordGame',
  'revealRule',
  'toast',
  'send',
  'fx',
  'phrases',
  'wakeLock',
  'orientationLock',
  'startHost',
  'startGuest',
  'closeNet',
  'confirm',
  'then',
  'initHome',
  'scrollTop',
  'startTimer',
  'cancelTimer',
  'toggleSound',
  'share',
  'fillName',
  'fillP2Name',
  'setCode',
] as const satisfies ReadonlyArray<ShellEffect<ShellTypes>['type']>;

/** A game's whole effect union: the shell's and its own. */
export type Effect<G extends ShellTypes> = ShellEffect<G> | G['Effect'];

const SHELL_EFFECTS: ReadonlySet<string> = new Set(SHELL_EFFECT_TYPES);
export const isShellEffect = <G extends ShellTypes>(effect: Effect<G>): effect is ShellEffect<G> =>
  SHELL_EFFECTS.has(effect.type);

export type Step<G extends ShellTypes> = Readonly<{
  app: ShellApp<G>;
  effects: ReadonlyArray<Effect<G>>;
}>;
export type Ctx = Readonly<{
  rng: Rng;
  now: () => number;
  /** `prefers-reduced-motion: reduce` on this device (web/shared/edge/motion.ts), so a reducer's timers and the painter's CSS agree; absent in tests and stories that do not care. */
  reducedMotion?: boolean;
  /**
   * `screen.orientation.lock` is a function on this device and no pointer of it hovers
   * (`(hover: none)`; web/shared/edge/boot.ts reads both once, false without a `screen` or a
   * `matchMedia`): Android's Chromium family, where Quick Settings' Auto-rotate locks the phone as
   * it is held. Absent on every iPhone browser, whose one switch, Portrait Orientation Lock, would
   * snap the page upright; false on a touchscreen laptop, whose Chromium has the function (it
   * rejects) but whose trackpad hovers. What the rotation hint (`rotationHint`) asks; absent in
   * tests and stories that do not care.
   */
  canLock?: boolean;
}>;

// ---- the config a game supplies (docs/design/shared-shell.md §4.3) ------------------------------

/**
 * Where a shared flow resets the table, so each game spells the reset it made at that site before
 * the move (agreed in C2, replacing the design's `cleared`/`handedOff`/`newView` sketch: gin's
 * sites clear different sets, and the spread at each site moves into the game's switch as it was):
 * a pass-and-play start (a position loaded into one resets as a start does: backgammon's
 * `tableCleared` at that site, dry-round-2.md F5), a deal, the handoff, a leave, the host lost, a
 * new view (`broadcast`, `localBroadcast`), an applied action (`hostDispatch`) and a guest's
 * `state` frame.
 */
export type TableReset =
  'startLocal' | 'deal' | 'handoff' | 'leave' | 'lost' | 'view' | 'applied' | 'frame';

/** A preference over the game's store: web/shared/edge/prefs.ts `TextPref` fits, typed loosely so this zone never names the edge. */
export type Pref<St, T> = Readonly<{
  read: (store: St) => Result<T, unknown>;
  write: (store: St, value: T) => unknown;
}>;

/**
 * The room's terms over the game's store (`ShellPrefs.opts`): `read` fills the game's defaults
 * where a key is missing or unreadable (so `readHome` never sees a failure), `write` stores every
 * term under its own key (a game's `writeOpts` as it was).
 */
export type OptsPref<St, T> = Readonly<{
  read: (store: St) => T;
  write: (store: St, opts: T) => unknown;
}>;

/** The finished games over the game's store (prefs.ts `RecentGamesPref` fits): the list or [], and one record put first. */
export type RecentGamesPref<St> = Readonly<{
  read: (store: St) => ReadonlyArray<RecentGame>;
  append: (store: St, game: RecentGame) => unknown;
}>;

/** The shell's readers and writers the game builds over its keys (prefs.ts `shellStore`). */
export type ShellPrefs<G extends ShellTypes> = Readonly<{
  /** The store's keys the boot reads by name: the sound font's, named in the console hook's refusal (prefs.ts `ShellKeys`). */
  keys: Readonly<{ soundFont: string }>;
  name: Pref<G['Store'], string>;
  p2Name: Pref<G['Store'], string>;
  homeTab: Pref<G['Store'], Tab<G>>;
  playMode: Pref<G['Store'], PlayMode>;
  /** The sound preference (prefs.ts `SOUND_STATES`): the boot's default fx persists `toggle` through it. */
  sound: Pref<G['Store'], 'on' | 'off'>;
  soundFont: Pref<G['Store'], SoundFontName>;
  /** The far seat's flip (prefs.ts `FLIP_STATES`): `readHome` reads it as a boolean, `writeFlip` writes it. */
  flipTable: Pref<G['Store'], FlipState>;
  recentGames: RecentGamesPref<G['Store']>;
  /** The room's terms remembered across reloads (the seat-count steppers, backgammon's two selects); absent, the game forgets them (gin, hive). */
  opts?: OptsPref<G['Store'], G['Opts']>;
  /** The pass-and-play names past the shell's two, index 0 the third seat (prefs.ts `extraNamePrefs`); absent, the game keeps none (the two-seat games). */
  seatNames?: ReadonlyArray<Pref<G['Store'], string>>;
  save: Readonly<{
    readSave: (store: G['Store']) => Result<Save<G>, unknown>;
    writeSave: (store: G['Store'], save: Save<G>) => unknown;
    clearSave: (store: G['Store']) => unknown;
  }>;
}>;

/** Everything the shared flows call where the two reducers differed, and every literal shared code may not contain. */
export type ShellConfig<G extends ShellTypes> = Readonly<
  ShellConfigBase<G> & HomeField<G, HomeHooks<G>>
>;

/** `ShellConfig` less `home`, whose presence turns on the game's `Home` (`HomeField`). */
export type ShellConfigBase<G extends ShellTypes> = Readonly<{
  /** The peer prefix and the room-code spec (web/shared/lib/roomCode.ts). */
  id: Game;
  /**
   * The way the game is played on a phone (docs/design/shared-shell.md "Playing sideways"):
   * `'landscape'` (backgammon's flat board, docs/design/backgammon-landscape.md) or `'portrait'`
   * (the mirror: UI Sandbox's portrait mode). With either, the boot watches `PORTRAIT_PHONE` and
   * `LANDSCAPE_PHONE` (web/shared/edge/media.ts) into `portraitPhone`/`landscapePhone`, the turn
   * gate (`gateOpen`, shellPaint.ts `paintGate` with `GATE_COPY[orientation]`'s words, the page's
   * `gateMarkup`) asks for a turn of the phone at the table when it is held the other way
   * (`wrongWay`), the Android lock holds that orientation (boot.ts hands it to
   * `createOrientationLock`) and the rotation hint speaks of it. `'any'` or absent (gin, briscola,
   * fidice): nothing is watched, no gate, the upright home as it is.
   */
  orientation?: PlayOrientation | 'any';
  /** Absent (every game so far), the shell's `DEFAULT_NAME` and `DEFAULT_GUEST_NAME`. */
  names?: Readonly<{
    /** The host name an empty input means, and the prefill of every name input; `DEFAULT_NAME` when absent. */
    default?: string;
    /** What a guest that typed nothing joins as (`guestName`); `DEFAULT_GUEST_NAME` when absent. */
    guest?: string;
  }>;
  /**
   * The pass-and-play seats' defaults, first seat first: shown in the inputs when nothing is
   * remembered and seated when they are left empty (the owner, 2026-09-25: "backgammon is Ari and
   * Ethan", "briscola is Ari and Lavi (with p3 Sandro and p4 Grant)"). Absent, the shell's
   * `DEFAULT_LOCAL_NAMES`; a seat past the list is `Player N` (`localNameFor`).
   */
  localNames?: ReadonlyArray<string>;
  /** The home tabs and the one shown first; absent, `HOME_TABS` and `DEFAULT_HOME_TAB` (`tabsOf`): a game with a tab of its own (gin, fidice) spells the list. */
  tabs?: Readonly<{ list: ReadonlyArray<Tab<G>>; default: Tab<G> }>;
  /** Absent, online by default and the two stored modes parsed as `defaultModeParse` reads them. */
  modes?: Readonly<{
    /** The mode shown before anything is stored; `DEFAULT_PLAY_MODE` when absent. */
    default?: PlayMode;
    /**
     * `mode/set`'s raw value: the mode shown and the one stored (null: shown only, gin's sandbox),
     * or null to ignore the intent (a sandbox the name does not unlock). Absent, `local` is
     * local and anything else online, shown and stored alike (`defaultModeParse`): a game with no
     * mode of its own leaves it out; gin (the sandbox) and fidice (Solo, Watch) spell theirs.
     */
    parse?: (
      raw: string,
      shell: ShellState<G>,
    ) => Readonly<{ shown: Mode<G>; stored: PlayMode | null }> | null;
  }>;
  copy: Readonly<{
    /** The leave confirms (seatCopy.ts `leaveCopy` spells them from the game's three nouns). */
    leaveLocal: string;
    leaveOnline: string;
    /** The sessions' status copy (web/shared/lib/shellDefaults.ts, which web/shared/net speaks too); each absent, the shell's (`copyOf`). */
    opening?: string;
    connecting?: (code: string) => string;
    handoff?: (code: string, oppName: string | null) => string;
    /**
     * The guest's status once the host's welcome or lobby frame names the room (gin names the
     * target). `seated` is how many are at the table, the host included, of `capacity` seats: an
     * N-seat game says "3 of 4 seated"; a two-seat game's takes the first two and is told 2 of 2.
     */
    hostRoom: (hostName: string, opts: G['Opts'], seated: number, capacity: number) => string;
    // ---- the N-seat forms (n-seat-sessions.md §7), each with the two-seat string as its default; a two-seat game leaves them out ----
    /** The host's open status with no hand dealt (`HostOptions.waiting`); absent, the session's WAITING_MSG. */
    waiting?: (capacity: number) => string;
    /** `name` joined the lobby; `names` are every seated guest in seat order, `remaining` the empty seats; absent, `joinedMsg(name)`. */
    joined?: (name: string, names: ReadonlyArray<string>, remaining: number) => string;
    /** Seat `seat` (`name`, or null for a seat never named) left the lobby; `seated` of `capacity` remain, the host counted; absent, OPPONENT_LEFT_MSG. */
    seatLeft?: (name: string | null, seat: number, seated: number, capacity: number) => string;
    /** Seat `seat`'s channel dropped mid-game; absent, `guestGoneMsg(name, code)`. */
    guestGone?: (name: string | null, code: string | null, seat: number) => string;
    /** The guest told the table is full; absent, ROOM_FULL_MSG. */
    roomFull?: string;
    /** `host/deal` with `seated` at the table (the host counted) and `min` needed; absent, WAITING_FOR_GUEST_MSG. */
    notEnough?: (seated: number, min: number) => string;
  }>;
  /**
   * A table of more than two seats (n-seat-sessions.md §7): the players a room may hold, the
   * host counted. Absent for the two-seat games, whose every flow is then the line it was. Set,
   * the room opens at `opts.capacity(opts)` seats (2..`max`; `max` when the game supplies no
   * reader), Start enables at `min` seated (or, with `fixed`, at the room's capacity: a fixed
   * table starts full, since briscola's three-seat deck cannot be dealt to two; `min` is then the
   * smallest table alone), the host's `send`/`startHost` effects name seats and the capacity, and
   * a guest reads its seat and the table off the room frames (`roomSeatingOf`: `you` and `seats`
   * beside the options, D3), which the game's protocol puts there.
   */
  seats?: Readonly<{ min: number; max: number; fixed?: boolean }>;
  opts: Readonly<{
    initial: G['Opts'];
    /** The raw select/input values off `host/click`/`local/click`; `current` is the shell's for a missing value. */
    parse: (raw: G['Raw'], current: G['Opts']) => G['Opts'];
    /** The terms a game was made under, for the handoff. */
    ofGame: (game: G['State']) => G['Opts'];
    /** The option fields alone off a record that carries them (a welcome frame, a resume offer), in the literal's order. */
    pick: (from: G['Opts']) => G['Opts'];
    /** An N-seat game: the seats a room under `opts` holds, the host's included (`HostOptions.capacity`; briscola its seat count). Read only with `cfg.seats`; absent then, `cfg.seats.max`. */
    capacity?: (opts: G['Opts']) => number;
  }>;
  engine: Readonly<{
    /**
     * The deal: the host first, then every guest seat in order (`PlayersOf<G>`: the pair for a
     * two-seat game), one per seat of the room's capacity (`capacityOf(opts)`): the shell seats
     * every chair before it deals (`localSeats` names an empty input, `startHost` opens
     * `capacity − 1` seats), so an engine deals to the list as it is and pads nothing.
     */
    create: (players: PlayersOf<G>, opts: G['Opts'], rng: Rng, now: () => number) => G['State'];
    apply: (
      game: G['State'],
      seat: SeatOf<G>,
      action: G['Action'],
      rng: Rng,
      now: () => number,
    ) => Result<G['State'], string>;
    viewFor: (game: G['State'], seat: SeatOf<G>) => G['View'];
    /** The view shows the game over: nothing to rejoin, nobody to toast for. */
    over: (view: G['View']) => boolean;
    /**
     * The view shows one game over while the match goes on (backgammon's result sheet, a card of
     * text over a board nobody plays: `phase === 'over'`), read upright and not gated (`gateOpen`);
     * absent, a game is over only when `over` says so.
     */
    gameOver?: (view: G['View']) => boolean;
    /** The saved game is over: not offered to resume. */
    finished: (game: G['State']) => boolean;
    /** The two seats' names, for the handoff (seat 0 hosts, seat 1 joins). */
    names: (game: G['State']) => Readonly<[string, string]>;
    /**
     * The game's own rule for the handoff, over the shell's two seats (`handoffable`): fidice
     * offers it for two humans and no computer. Absent, every two-seat game may go online.
     */
    handoffable?: (game: G['State']) => boolean;
    /** `game.players[seat].name = name` on a rejoin; `seat` is 1 for a two-seat game, whose builder takes the first two. */
    renameGuest: (game: G['State'], name: string, seat: SeatOf<G>) => G['State'];
    /** The engine state off `position/load`'s hand-made object (the save's decoder); its error names the path. */
    decodeState: Decoder<G['State']>;
  }>;
  /**
   * What a finished game leaves in the device's history (web/shared/lib/recentGames.ts), read off
   * the view the first time `engine.over` says so: the game's identity (`keyOf`, its start time,
   * so a re-sent frame or a repaint records nothing and a rematch records again), every seat's
   * name in seat order (`playersOf`: the view has them for every role, and a game with more than
   * two seats names them all), its final score as the game spells it (gin "104–87", backgammon
   * "5–3") and the winner, or null when nobody won: a seat, or the side seat 0 is on where a game
   * plays in sides (briscola's teams), so `outcomeFor` reads it against the user's seat either way.
   */
  result: Readonly<{
    playersOf: (view: G['View']) => ReadonlyArray<string>;
    /**
     * My seat's name off a view, null when the view has no row for it; absent,
     * `playersOf(view)[seat]` (the games whose players sit in shell-seat order: gin, backgammon,
     * briscola). A game whose view's players are chairs, not seats, supplies it (fidice: a
     * watching host holds no chair).
     */
    seatName?: (view: G['View'], seat: SeatOf<G>) => string | null;
    scoreOf: (view: G['View']) => string;
    winnerOf: (view: G['View']) => SeatOf<G> | null;
  }> &
    KeyOfField<G>;
  /** The game's protocol.ts builders the shell sends: its `PROTOCOL` record whole (`seatedProtocol`, `twoSeatProtocol`), of which these five are read. */
  frames: Readonly<{
    /** The lobby frame for seat `you`: an N-seat game's carries the seat list and the receiver's seat (D3); a two-seat game's takes the first two. */
    lobby: (
      hostName: string,
      opts: G['Opts'],
      seats: ReadonlyArray<SeatState>,
      you: SeatOf<G>,
    ) => HostFrameOf<G>;
    state: (view: G['View']) => HostFrameOf<G>;
    toast: (message: string) => HostFrameOf<G>;
    action: (action: G['Action']) => GuestFrameOf<G>;
    /**
     * The guest's join under `name` (the session sends the first at channel open, web/shared/net/guest.ts;
     * the shell sends another on `name/rename`, which the host takes as a re-seat).
     */
    join: (name: string) => GuestFrameOf<G>;
  }>;
  cues: Readonly<{
    /** The cue table (ui/sound.ts, event -> cue + buzz) the boot's default fx plays (cuePlayer.ts `shellFx`). */
    table: Readonly<Record<Cue<G>, Phrase>>;
  }> &
    InitialCuesField<G>;
  table: Readonly<{
    initial: G['Table'];
    /**
     * The table at each shared reset site (`TableReset`). Absent, the default (`tableReset`): the
     * initial table with `keeps` carried over where the site starts over, the table as it is
     * elsewhere; a game whose sites all read that way (uno) spells only `keeps`.
     */
    reset?: (table: G['Table'], at: TableReset) => G['Table'];
    /**
     * What the default reset carries from the old table into the fresh one at a start, the
     * handoff, a leave and the host lost: the fields that are a preference of the device, not of
     * the game (uno's extra names). Absent, nothing.
     */
    keeps?: ReadonlyArray<keyof G['Table']>;
    /**
     * The state side of a paint against the view: `prev` is the view it replaces, `ctx` the clock
     * for backgammon's R14 beat, read by the game that needs it and never here (gin's DOM parity
     * oracle drives both pages with a counting clock, so an eager `ctx.now()` per paint shows up
     * as elapsed time).
     */
    rendered: (app: ShellApp<G>, prev: G['View'] | null, ctx: Ctx) => Step<G>;
    /**
     * The pause a new view raises against the one it replaces (`ShellState.pause`; flip7's
     * `pauseFor`: the first seat that went from active to busted, frozen or a Flip 7), or null.
     * Read after `rendered` at every paint while no pause is up; a cold paint (`prev` null: a
     * resume, a reconnect) is the adapter's to refuse. Absent, the game never pauses.
     */
    pause?: (app: ShellApp<G>, prev: G['View'] | null, view: G['View']) => Pause | null;
    /**
     * A refused action: the toast, and whatever the table drops (gin a waiting draw stage,
     * backgammon its taps, hive its pick). Absent, the toast alone (`refuse`): a table with
     * nothing picked up to drop (uno, flip7) leaves it out.
     */
    refuse?: (app: ShellApp<G>, message: string) => Step<G>;
    /**
     * Escape with no sheet open (a pause up took it first): what of the game's own goes first
     * (briscola's card view, drag, lift and deck; hive's peek, proposal and pick), or null when nothing of its
     * own is up, so the shell closes the history, then the rules. Absent, the shell's two alone.
     */
    escape?: (app: ShellApp<G>, ctx: Ctx) => Step<G> | null;
    /**
     * An ephemeral frame arrived (`host/frame` from the guest's channel, `guest/frame` from the
     * host): `seat` is the sender's channel seat as the shell knows it (1 for the host's one guest,
     * 0 for the host at a guest). Absent, the frame is dropped: nothing in the shell reads it.
     */
    ephemeral?: (app: ShellApp<G>, frame: EphemeralOf<G>, seat: SeatOf<G>, ctx: Ctx) => Step<G>;
  }>;
  /** Pass-and-play's two game-specific decisions (agreed in C2). */
  local: Readonly<{
    /**
     * `localBroadcast`: whose view is shown, whether the curtain comes up for them, and what else
     * the change hands over (backgammon's hit toast when the curtain is off; absent, nothing).
     */
    viewer: (
      app: ShellApp<G>,
      game: G['State'],
    ) => Readonly<{
      seat: SeatOf<G>;
      curtain: SeatOf<G> | null;
      effects?: ReadonlyArray<Effect<G>>;
    }>;
    /**
     * `curtain/reveal`: the seat that lifts the curtain and what it is told (backgammon's hits
     * against it; absent, nothing). Its seat is also who acts from this phone (`act`): the actor
     * while the game is on, whoever holds the phone between rounds.
     */
    revealer: (
      game: G['State'],
    ) => Readonly<{ seat: SeatOf<G>; effects?: ReadonlyArray<Effect<G>> }>;
    /**
     * An applied action began a new game (uno's and hive's `again`, the next game of a briscola or
     * backgammon match): `act` clears the reveal, so the curtain names the new game's starter even
     * when the seat that tapped it is the one to begin. Absent, a reveal outlives every action.
     */
    newGame?: (prev: G['State'], next: G['State']) => boolean;
    /**
     * The seat a pass-and-play view is for (backgammon `view.me.idx`): who is looking at the
     * phone while the curtain is down, what `flipped` reads to turn the table for seat 1. A game
     * without the flip setting leaves it out, and its table never turns.
     */
    holder?: (view: G['View']) => SeatOf<G>;
  }>;
  prefs: ShellPrefs<G>;
}>;

/** The home screen's game half: what `initHome` reads and applies beyond the shell's own keys. */
export type HomeHooks<G extends ShellTypes> = Readonly<{
  /** The game's own keys for the snapshot (`G['Home']`). */
  read: (store: G['Store']) => G['Home'];
  /** The snapshot's own part into the App (gin's sort and card back onto the table; backgammon's options into the shell and its curtain mode onto the table). */
  apply: (app: ShellApp<G>, home: HomeSnapshot<G>) => ShellApp<G>;
  /**
   * The resume box for a snapshot, in the game's order of precedence (gin's scorer first).
   * Absent, the shell's three offers off the save (`resumeFor(home.save, cfg)`): a game with no
   * offer of its own leaves it out.
   */
  resume?: (home: HomeSnapshot<G>) => Resume<G> | null;
  /** `resume/click` on an offer the shell does not know (`G['Resume']`); absent, nothing happens (a game whose `Resume` is `never` leaves it out). */
  resumeExtra?: (app: ShellApp<G>, offer: G['Resume'], ctx: Ctx) => Step<G>;
}>;

// ---- the fields a game spells only when the shell's default is not its type -------------------
// Each is required exactly when the shell could not supply the value: the type says so, and the
// one reader of each (`homeOf`, `initialCuesOf`, `keyOfView`) carries the one cast that fact
// licenses, so the flows read a plain value.

/** `home`, optional when the game's `Home` is the bare `object` (uno, flip7: nothing to read, nothing to apply). */
export type HomeField<G extends ShellTypes, H> = [object] extends [G['Home']]
  ? Readonly<{ home?: H }>
  : Readonly<{ home: H }>;
/** `cues.initial`, optional when the game's memory is the plain `CueMemory` (every game but gin, which adds `turnKey`). */
export type InitialCuesField<G extends ShellTypes> = [CueMemory] extends [G['Cues']]
  ? Readonly<{ initial?: G['Cues'] }>
  : Readonly<{ initial: G['Cues'] }>;
/** A view stamped with its game's start, the key every game but fidice records under. */
export type StartedView = Readonly<{ startedAt: number }>;
/** `result.keyOf`, optional when the view carries `startedAt` (`String(view.startedAt)` is then the key). */
export type KeyOfField<G extends ShellTypes> = G['View'] extends StartedView
  ? Readonly<{ keyOf?: (view: G['View']) => string }>
  : Readonly<{ keyOf: (view: G['View']) => string }>;

/** `HOME_TABS` and `DEFAULT_HOME_TAB` as a config's `tabs`: the three every page carries fit any game's `Tab<G>`. */
export const SHELL_TABS = { list: HOME_TABS, default: DEFAULT_HOME_TAB } as const;
/** The game's `tabs`, or the shell's three. */
export const tabsOf = <G extends ShellTypes>(
  cfg: Readonly<{ tabs?: Readonly<{ list: ReadonlyArray<Tab<G>>; default: Tab<G> }> }>,
): Readonly<{ list: ReadonlyArray<Tab<G>>; default: Tab<G> }> => cfg.tabs ?? SHELL_TABS;
/** The host name an empty input means: the game's, or `DEFAULT_NAME`. */
export const defaultNameOf = (cfg: Readonly<{ names?: Readonly<{ default?: string }> }>): string =>
  cfg.names?.default ?? DEFAULT_NAME;
/** The mode shown before anything is stored: the game's, or online. */
export const defaultModeOf = (
  cfg: Readonly<{ modes?: Readonly<{ default?: PlayMode }> }>,
): PlayMode => cfg.modes?.default ?? DEFAULT_PLAY_MODE;
/** The sessions' three status strings the shell paints before a session speaks (`ShellConfig['copy']`'s optional three). */
export type SessionCopy = Readonly<{
  opening: string;
  connecting: (code: string) => string;
  handoff: (code: string, oppName: string | null) => string;
}>;
/** The sessions' three status strings: the game's where it has words of its own, else the shell's. */
export const copyOf = (cfg: Readonly<{ copy: Readonly<Partial<SessionCopy>> }>): SessionCopy => ({
  opening: cfg.copy.opening ?? OPENING_MSG,
  connecting: cfg.copy.connecting ?? connectingMsg,
  handoff: cfg.copy.handoff ?? handoffMsg,
});
/** The game's `home` hooks, or the shell's: nothing read, the App as it is. */
export const homeOf = <G extends ShellTypes>(
  cfg: Readonly<{ home?: HomeHooks<G> }>,
): HomeHooks<G> =>
  // `HomeField` makes `home` optional only when `object` is the game's `Home`, which `{}` is.
  cfg.home ?? { read: () => ({}), apply: (app) => app };
/** The cue memory before any paint: the game's, or `INITIAL_CUE_MEMORY`. */
export const initialCuesOf = <G extends ShellTypes>(
  cfg: Readonly<{ cues: Readonly<{ initial?: G['Cues'] }> }>,
): G['Cues'] =>
  // `InitialCuesField` makes `initial` optional only when `CueMemory` is the game's `Cues`.
  cfg.cues.initial ?? INITIAL_CUE_MEMORY;
/** The finished game's key (`result.keyOf`): the game's, or its view's `startedAt`. */
export const keyOfView = <V>(
  cfg: Readonly<{ result: Readonly<{ keyOf?: (view: V) => string }> }>,
  view: V,
): string => (cfg.result.keyOf === undefined ? startedKey(view) : cfg.result.keyOf(view));
/** `String(view.startedAt)`: `KeyOfField` leaves `keyOf` out only for a view that is a `StartedView`, which licenses the one assertion. */
const startedKey = (view: unknown): string => String((view as StartedView).startedAt);

/**
 * The half of a config a game spells from its engine, protocol and storage alone
 * (`src/shellConfig.ts`); its `ui/state.ts` adds the table hooks, which use the reducer's own
 * helpers, and completes `home`.
 */
export type ShellGameData<G extends ShellTypes> = Readonly<
  Omit<ShellConfigBase<G>, 'table' | 'local'> &
    ([object] extends [G['Home']]
      ? Readonly<{ home?: never }>
      : Readonly<{ home: Pick<HomeHooks<G>, 'read'> }>)
>;

// ---- the strings the shell (not the sessions) writes, the same in both games -------------------

/** The Play tab opens its submenu after this long a press. */
export const LONG_PRESS_MS = 450;
/**
 * A waiting room saved (opened or reopened) within this long resumes by itself at boot
 * (`resume/auto`; lobby-resume.md D4): long enough for a phone to discard the tab while the host
 * chats the invite around, short enough that a room left open days ago never opens itself and
 * is offered instead.
 */
export const WAITING_RESUME_MS = 30 * 60_000;
/** The guest wait screen's first status, before the session speaks. */
export const CONNECTING_MSG = 'Connecting…';
export const NOT_CONNECTED_MSG = 'Not connected to the host.';
export const WAITING_FOR_GUEST_MSG = 'Waiting for your opponent to join.';
export const OPPONENT_LEFT_MSG = 'Opponent left. Waiting for someone to join…';
export const ROOM_FULL_MSG = 'That room already has two players.';
export const LOST_HOST_MSG = 'Lost connection to the host — reconnecting…';
export const DISCONNECTED_MSG = 'Disconnected from the host — reconnecting…';
export const joinedMsg = (name: string): string => `${name} joined! Ready when you are.`;
export const guestGoneMsg = (oppName: string | null, code: string | null): string =>
  `${oppName ?? 'Opponent'} disconnected — they can rejoin with code ${String(code)}.`;
/** `onGuestGone`'s toast lasts this long, as does `LOST_HOST_MSG`. */
export const GONE_TOAST_MS = 4000;
/** `position/load` outside pass-and-play, and a position the decoder refuses (backgammon's copy before dry-round-2.md F5; gin showed none: its sandbox deals, never loads). */
export const SANDBOX_LOCAL_ONLY_MSG = 'Positions can only be set up in pass-and-play.';
export const badPositionMsg = (error: string): string => `That position is not valid: ${error}`;

// ---- steps and the small helpers every case uses; the games import them for their table cases ----

export const pure = <G extends ShellTypes>(app: ShellApp<G>): Step<G> => ({ app, effects: [] });
export const step = <G extends ShellTypes>(
  app: ShellApp<G>,
  ...effects: ReadonlyArray<Effect<G>>
): Step<G> => ({ app, effects });
/**
 * Run `f` after `s`, keeping `s`'s effects first. Named `andThen` here and imported as `then` by
 * the games: an export called `then` would make this module's namespace a thenable, which a test
 * runner's dynamic import awaits (and rejects with nothing).
 */
export const andThen = <G extends ShellTypes>(
  s: Step<G>,
  f: (app: ShellApp<G>) => Step<G>,
): Step<G> => {
  const next = f(s.app);
  return { app: next.app, effects: [...s.effects, ...next.effects] };
};
/**
 * The toast's kinds beyond the plain one: `error` is red and read out at once (shellPaint.ts
 * `showToast`: the `error` class, `role="alert"`), for a refusal the player must read (Hive's
 * hints-off play: "yell at you with a red toast and tell you why it's no good").
 */
export type ToastKind = 'error';
/** An error toast lasts this long: a sentence to read, not a notice to glance at. */
export const ERROR_TOAST_MS = 4000;
export const toast = (
  message: string,
  ms: number | null = null,
  kind?: ToastKind,
): Readonly<{ type: 'toast'; message: string; ms: number | null; kind?: ToastKind }> => ({
  type: 'toast',
  message,
  ms,
  ...(kind === undefined ? {} : { kind }),
});
/** The red toast: `message` as an error for ERROR_TOAST_MS. */
export const errorToast = (
  message: string,
): Readonly<{ type: 'toast'; message: string; ms: number | null; kind?: ToastKind }> =>
  toast(message, ERROR_TOAST_MS, 'error');

// ---- the config's defaults: the body every game but one spelled (shell-call-graph.md §4.3) --------

/**
 * A refused action, `table.refuse` absent: the toast alone; the table has nothing picked up to
 * drop. Exported for the game whose own guard refuses before `act` (flip7's guest between rounds).
 */
export const refuse = <G extends ShellTypes>(app: ShellApp<G>, message: string): Step<G> =>
  step(app, toast(message));
/** `cfg.table.refuse(app, message)`, or the default. */
const refuseWith = <G extends ShellTypes>(
  app: ShellApp<G>,
  message: string,
  cfg: ShellConfig<G>,
): Step<G> => (cfg.table.refuse ?? refuse)(app, message);
/**
 * The reset sites that start the table over (a pass-and-play start, the handoff, a leave, the
 * host lost) against the ones that keep it (a deal, a view, an applied action, a guest's frame):
 * the split every game's `reset` drew before the default took it (shell-hoist.md row P).
 */
const STARTS_OVER: Readonly<Record<TableReset, boolean>> = {
  startLocal: true,
  handoff: true,
  leave: true,
  lost: true,
  deal: false,
  view: false,
  applied: false,
  frame: false,
};
export const startsOver = (at: TableReset): boolean => STARTS_OVER[at];
/**
 * `table.reset` absent: at a site that starts over, the initial table with `keeps` carried from
 * the old one; elsewhere the table as it is.
 */
const tableReset =
  <T extends object>(initial: T, keeps: ReadonlyArray<keyof T>) =>
  (table: T, at: TableReset): T =>
    startsOver(at) ? { ...initial, ...Object.fromEntries(keeps.map((k) => [k, table[k]])) } : table;
/** `cfg.table.reset(table, at)`, or the default over `cfg.table.initial` and `cfg.table.keeps`. */
const resetWith = <G extends ShellTypes>(
  table: G['Table'],
  at: TableReset,
  cfg: ShellConfig<G>,
): G['Table'] =>
  (cfg.table.reset ?? tableReset(cfg.table.initial, cfg.table.keeps ?? []))(table, at);
/** `modes.parse` absent: `local` is local and anything else online, shown and stored alike. */
const defaultModeParse = (raw: string): Readonly<{ shown: PlayMode; stored: PlayMode }> => {
  const mode: PlayMode = raw === 'local' ? 'local' : 'online';
  return { shown: mode, stored: mode };
};
const tap = { type: 'fx', cue: 'tap' } as const;
export const withShell = <G extends ShellTypes>(
  app: ShellApp<G>,
  over: Partial<ShellState<G>>,
): ShellApp<G> => ({ ...app, shell: { ...app.shell, ...over } });
export const withTable = <G extends ShellTypes>(
  app: ShellApp<G>,
  over: Partial<G['Table']>,
): ShellApp<G> => ({ ...app, table: { ...app.table, ...over } });

// ---- playing sideways: the turn gate (docs/design/backgammon-landscape.md §5D) ------------------

/** What `gateOpen` reads of the shell: the screen, the view, the two phone predicates, the dismissal and the lock. */
export type GateState<G extends ShellTypes> = Pick<
  ShellState<G>,
  'screen' | 'view' | 'portraitPhone' | 'landscapePhone' | 'gateDismissed' | 'orientationLocked'
>;
/** What `gateOpen` reads of the config: the opt-in and the two over predicates; a `ShellConfig` fits. */
export type GateConfig<G extends ShellTypes> = Readonly<{
  orientation?: PlayOrientation | 'any';
  engine: Pick<ShellConfig<G>['engine'], 'over' | 'gameOver'>;
}>;

/** The one way a game is played on a phone: sideways (backgammon) or upright (UI Sandbox's portrait mode); `ShellConfig.orientation` less `'any'`. */
export type PlayOrientation = 'landscape' | 'portrait';

/** The way a config plays, or null where it plays either way (`'any'`, or the key absent: gin, briscola, fidice). */
export const playsOrientation = (
  cfg: Readonly<{ orientation?: PlayOrientation | 'any' }>,
): PlayOrientation | null =>
  cfg.orientation === 'landscape' || cfg.orientation === 'portrait' ? cfg.orientation : null;

/** What `wrongWay` reads of the shell: the two phone predicates as the boot's watchers reported them. */
export type PhoneWay = Pick<ShellState<ShellTypes>, 'portraitPhone' | 'landscapePhone'>;

/**
 * The phone is held the other way from the one the game plays (the turn gate's reason, shared with
 * UI Sandbox, which has no App): upright where the game plays sideways (`portraitPhone`), sideways
 * where it plays upright (`landscapePhone`); never where it plays either way, and never on a phone
 * neither watcher has reported (a desktop window matches neither predicate).
 */
export const wrongWay = (orientation: PlayOrientation | 'any' | undefined, s: PhoneWay): boolean =>
  orientation === 'landscape'
    ? s.portraitPhone
    : orientation === 'portrait'
      ? s.landscapePhone
      : false;

/** The phone is held the way the game plays: where the rotation hint is due. */
const rightWay = (orientation: PlayOrientation, s: PhoneWay): boolean =>
  orientation === 'landscape' ? s.landscapePhone : s.portraitPhone;

/**
 * The turn gate is up (shellPaint.ts `paintGate`, painted by the boot after the game's own paint):
 * in a game that plays one way, at the table, on a phone held the other way (`wrongWay`), while a
 * game is on, until the phone turns or "Play upright" (or "Play sideways") for this table, and never while the Android lock is held
 * (`orientationLocked`: the tap that took it is turning the phone, so the sheet would only flash
 * over the curtain until the watcher sees the turn; `fullscreen/lost` clears the mark, and a phone
 * still upright then gets the gate back). The home, the waiting rooms and the endgame stay
 * upright-friendly, and so does a finished game (`engine.over`, or `engine.gameOver` where the
 * game has one: backgammon's result sheet, read fine upright and handed over upright; the next
 * game's first curtain brings the gate back). Not a modal in the reducer's sense: taps still
 * reduce; `inert` on the DOM is the guard.
 */
export const gateOpen = <G extends ShellTypes>(s: GateState<G>, cfg: GateConfig<G>): boolean =>
  wrongWay(cfg.orientation, s) &&
  s.screen === 'tableScreen' &&
  s.view !== null &&
  !cfg.engine.over(s.view) &&
  cfg.engine.gameOver?.(s.view) !== true &&
  !s.gateDismissed &&
  !s.orientationLocked;

// ---- the far seat's flip (docs/design/backgammon-landscape.md §6 item 7) ------------------------

/** What `flipped` reads of the shell: the role, the setting, the screen and the view. */
export type FlipShell<G extends ShellTypes> = Pick<
  ShellState<G>,
  'role' | 'flipForFar' | 'screen' | 'view'
>;
/** What `flipped` reads of the App: the shell's four fields and the table's curtain (`ShellTypes.Table`); a `ShellApp` fits. */
export type FlipApp<G extends ShellTypes> = Readonly<{
  shell: FlipShell<G>;
  table: Readonly<{ curtain: number | null }>;
}>;
/** What `flipped` reads of the config: the game's `holder` hook; a `ShellConfig` fits. */
export type FlipConfig<G extends ShellTypes> = Readonly<{
  local?: Pick<ShellConfig<G>['local'], 'holder'>;
}>;

/**
 * The page is turned 180° for the seat across the table (shellPaint.ts `paintFlip`, painted by
 * the boot after the game's own paint as `data-flip="1"` on the body; shell.css rotates the body,
 * which at the table is the viewport, so every fixed overlay, the toast, the flyers and the drag
 * ghost turn with it and hit-testing follows the transform; the two kernels that place a clone
 * on the body by measured rects reflect them into the turned space, dom.ts `bodySpace`). In
 * pass-and-play alone, with `flipForFar` on, at the table (where the body is the viewport,
 * `fixed-screen`; the endgame scrolls upright), when the seat now looking at the phone is seat
 * 1: the seat the curtain is up for while it is up (the phone is being handed to them, and the
 * curtain is what they read), else the seat whose view is shown (`cfg.local.holder` of the view;
 * a game without the hook never turns). Never online, never at home, never for seat 0, who laid
 * the phone down.
 */
export const flipped = <G extends ShellTypes>(app: FlipApp<G>, cfg: FlipConfig<G>): boolean => {
  const s = app.shell;
  if (s.role !== 'local' || !s.flipForFar || s.screen !== 'tableScreen') return false;
  const holder = cfg.local?.holder;
  const facing =
    app.table.curtain ?? (s.view === null || holder === undefined ? null : holder(s.view));
  return facing === 1;
};

/**
 * The rotation hint (the owner, 2026-09-28: "can we give a warning to lock the phone's rotation
 * in landscape mode if we haven't already?", "also important for solo play"): toasted once per
 * table, in every role, the first time the shell paints the table with the phone sideways on a
 * device that can lock its rotation (`Ctx.canLock`: `screen.orientation.lock` is a function and
 * no pointer hovers, Android's Chromium family, where Quick Settings' Auto-rotate locks the phone
 * as it is held; an iPhone has only Portrait Orientation Lock, which would snap the page upright,
 * so it never sees the hint; a touchscreen laptop's trackpad hovers, so nor does it). The copy is
 * Android's gesture and names the state to reach, Auto-rotate off, not the tile's tap, so a
 * player who locked the phone sideways already is not told to undo it; `ROTATION_HINT_MS` outlasts
 * the default toast, since the player reaches for Quick Settings, and a shorter toast over it
 * (the path toast, online) gives it back its remainder (toast.ts `createToaster`). No markup of
 * its own: the shell's `toast`.
 */
export const ROTATION_HINT_MSG =
  "Lock the phone's rotation so the board stays sideways: swipe down and make sure Auto-rotate is off.";
/** The mirror, for a game that plays upright (`orientation: 'portrait'`): the same gesture, the other way to hold the phone. */
export const ROTATION_HINT_UPRIGHT_MSG =
  "Lock the phone's rotation so the page stays upright: swipe down and make sure Auto-rotate is off.";
export const ROTATION_HINT_MS = 8000;
/** The hint's words for the way a game plays. */
const rotationHintMsg = (orientation: PlayOrientation): string =>
  orientation === 'landscape' ? ROTATION_HINT_MSG : ROTATION_HINT_UPRIGHT_MSG;

/**
 * The hint's step over an App just painted or just turned: the toast and the mark, or the App as
 * it is. Read at every `painted` and at `viewport/landscape`, so a table that came up upright (the
 * gate up) gets it at the turn of the phone, not at the next move. Silent once shown for this
 * table (`rotationHintShown`), off the table, with the phone upright or unreported, on a device
 * that cannot lock, and in a game that stays upright (no `cfg.orientation`).
 */
const rotationHint = <G extends ShellTypes>(
  app: ShellApp<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  const plays = playsOrientation(cfg);
  const due =
    plays !== null &&
    s.screen === 'tableScreen' &&
    rightWay(plays, s) &&
    ctx.canLock === true &&
    !s.orientationLocked &&
    !s.rotationHintShown;
  return plays !== null && due
    ? step(
        withShell(app, { rotationHintShown: true }),
        toast(rotationHintMsg(plays), ROTATION_HINT_MS),
      )
    : pure(app);
};

// ---- playing sideways: the Android lock (docs/design/backgammon-landscape.md §5C) ------------------

/**
 * The Android lock's step at a tap (the owner, 2026-09-25: "it should lock the user into place to
 * make it sideways. Only on mobile!"): the `orientationLock` effect, held, and the mark, or the
 * App as it is. A web page cannot lock an iPhone (no `screen.orientation.lock`, no element
 * fullscreen); Android's Chromium family can, only inside fullscreen and only from a tap, since
 * `requestFullscreen` needs transient activation, so the effect is stepped at the taps a game is
 * played through and nowhere else: the pass-and-play Start (`startLocal`), the host's deal, the
 * guest's Sit down (`join/click`; the invite link at boot is no tap), the home's Resume, the
 * curtain's Roll (`curtain/reveal`), the gate's own "Go sideways" (`gate/turn`), and, in
 * backgammon, the roll modal's CTA (`roll/click`). Once per loss, not at every turn: silent while
 * `orientationLocked`, which `fullscreen/lost` (a back gesture, a failed attempt) clears so the
 * next tap re-enters. Never against "Play upright" (`gateDismissed`): the gate offers the lock and
 * the upright board as the player's choice for this table (docs/design/backgammon-landscape.md §6
 * item 11), so after it no tap of this table locks the phone again; the next table's start, load,
 * handoff, leave or cancel clears the mark, and `gate/turn` never sees it set, since its gate was
 * up. Nothing on a device that cannot lock (`Ctx.canLock`: every iPhone, every desktop) and
 * nothing in a game that stays upright (no `cfg.orientation`), so every other game's effect lists
 * are what they were.
 */
export const lockSideways = <G extends ShellTypes>(
  app: ShellApp<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const due =
    playsOrientation(cfg) !== null &&
    ctx.canLock === true &&
    !app.shell.orientationLocked &&
    !app.shell.gateDismissed;
  return due
    ? step(withShell(app, { orientationLocked: true }), { type: 'orientationLock', hold: true })
    : pure(app);
};

/**
 * The lock's drop, for a leave or a cancel: the effect on a device that can lock in a game that
 * plays sideways, whether or not the state still says held (a lock lost to a back gesture left
 * nothing to drop, and the adapter is silent about it; a fullscreen whose lock was refused is
 * still fullscreen, and this is what leaves it); nothing anywhere else.
 */
const unlockSideways = <G extends ShellTypes>(
  ctx: Ctx,
  cfg: ShellConfig<G>,
): ReadonlyArray<ShellEffect<G>> =>
  playsOrientation(cfg) !== null && ctx.canLock === true
    ? [{ type: 'orientationLock', hold: false }]
    : [];

/** `(value.trim() || fallback).slice(0, 20)`. */
const nameOr = (raw: string, fallback: string): string => {
  const trimmed = raw.trim();
  return (trimmed === '' ? fallback : trimmed).slice(0, NAME_MAX);
};

/**
 * The name a guest joins under (the owner, 2026-09-28: the client defines its own name): what its
 * input shows, cut to NAME_MAX then trimmed (the host's order, protocol.ts `guestNameFor`, so the
 * string on the wire is the string the host seats), whenever that leaves anything, an untouched
 * prefill included (as `host/click` counts it); else the game's guest fallback. Chosen here, on the
 * guest, so the host never names a guest: its `guestNameAmong` only dedupes.
 */
export const guestName = (
  raw: string,
  cfg: Readonly<{ names?: Readonly<{ guest?: string }> }>,
): string => {
  const typed = raw.slice(0, NAME_MAX).trim();
  return typed === '' ? (cfg.names?.guest ?? DEFAULT_GUEST_NAME) : typed;
};

/**
 * The pass-and-play seats when their inputs are empty, and what `initHome` fills the inputs with
 * when nothing is remembered (the owner, 2026-09-25: "Make the default p1 ari and p2 lavi",
 * spelled as the proper names; the four more are the seats flip7 named, the longest list a game
 * kept), unless the game names its own (`ShellConfig.localNames`: the owner, later that day,
 * "backgammon is Ari and Ethan"). The first is `DEFAULT_NAME`, the online name input's markup
 * default, since `fillName` reaches that input too and must find the name it already shows. A
 * seat past the list is `Player N`.
 */
export const DEFAULT_LOCAL_NAMES: ReadonlyArray<string> = [
  DEFAULT_NAME,
  'Lavi',
  'Sandro',
  'Grant',
  'Noa',
  'Ethan',
];

/** The game's pass-and-play defaults (`localNames`), or the shell's two. */
export const localNamesOf = (
  cfg: Readonly<{ localNames?: ReadonlyArray<string> }>,
): ReadonlyArray<string> => cfg.localNames ?? DEFAULT_LOCAL_NAMES;

/** The default for seat `seat` (0-based): the list's name, else `Player N`. */
export const localNameFor = (names: ReadonlyArray<string>, seat: number): string =>
  names[seat] ?? `Player ${String(seat + 1)}`;

/** Two players from the pass-and-play inputs: the game's defaults (`localNamesOf`), and " 2" on a clash (gin's sandbox deals them too). */
export const localPlayers = (
  p1raw: string,
  p2raw: string,
  names: ReadonlyArray<string>,
): Readonly<[Player, Player]> => {
  const p1 = nameOr(p1raw, localNameFor(names, 0));
  const p2 = nameOr(p2raw, localNameFor(names, 1));
  return [
    { id: 'p1', name: p1 },
    { id: 'p2', name: p2.toLowerCase() === p1.toLowerCase() ? `${p2} 2` : p2 },
  ];
};

/**
 * The seats of a pass-and-play table with any number of players, from its name inputs in seat
 * order: `localPlayers`'s rule at every seat (the game's default for an empty seat, `localNameFor`:
 * its `localNames` as far as they go, `Player N` beyond; ` N` appended to a name an earlier seat
 * already has, case-insensitively), so a game with more than two seats names them as the two-seat
 * games do and `localSeats([p1, p2], names)` is `localPlayers(p1, p2, names)`. The game with the
 * extra seats creates its own engine state from these and hands it to `startLocal`.
 */
export const localSeats = (
  raws: ReadonlyArray<string>,
  names: ReadonlyArray<string>,
): ReadonlyArray<Player> =>
  raws.reduce<ReadonlyArray<Player>>((seated, raw, i) => {
    const n = String(i + 1);
    const name = nameOr(raw, localNameFor(names, i));
    const taken = seated.some((p) => p.name.toLowerCase() === name.toLowerCase());
    return [...seated, { id: `p${n}`, name: taken ? `${name} ${n}` : name }];
  }, []);

/** `ShellState.seatNames` with nothing remembered: one null per seat past the shell's two up to `cfg.seats.max`; [] for a two-seat game. */
const noSeatNames = <G extends ShellTypes>(
  cfg: Pick<ShellConfig<G>, 'seats'>,
): ReadonlyArray<string | null> =>
  Array.from({ length: cfg.seats === undefined ? 0 : Math.max(0, cfg.seats.max - 2) }, () => null);

const showScreen = <G extends ShellTypes>(app: ShellApp<G>, screen: ScreenId<G>): Step<G> =>
  step(withShell(app, { screen }), { type: 'scrollTop' });

const withHostStatus = <G extends ShellTypes>(
  app: ShellApp<G>,
  text: string,
  stopPulse = false,
): ShellApp<G> =>
  withShell(app, { hostStatus: { text, pulse: stopPulse ? false : app.shell.hostStatus.pulse } });
const withGuestStatus = <G extends ShellTypes>(
  app: ShellApp<G>,
  text: string,
  stopPulse = false,
): ShellApp<G> =>
  withShell(app, {
    guestStatus: { text, pulse: stopPulse ? false : app.shell.guestStatus.pulse },
  });

// ---- seats (docs/design/n-seat-sessions.md §7; every helper is the two-seat line at one seat) ----

/** A room of more than two seats: the game supplies `cfg.seats`; absent, every flow below takes the two-seat line it was. */
const isNSeat = <G extends ShellTypes>(cfg: ShellConfig<G>): boolean => cfg.seats !== undefined;

/**
 * The room's seats as the host's flows read them. An N-seat game's are `shell.seats`; a two-seat
 * game's are the legacy pair `oppName`/`oppConnected` (its suites build shells from those two
 * fields alone, so they stay its source of truth and `shell.seats` is their mirror).
 */
const seatsOf = <G extends ShellTypes>(
  s: ShellState<G>,
  cfg: ShellConfig<G>,
): ReadonlyArray<SeatState> =>
  isNSeat(cfg) ? s.seats : [{ name: s.oppName, connected: s.oppConnected }];

/** The seats written, and `oppName`/`oppConnected` with them as seat 1's (the host's reading of the two). */
const withSeats = <G extends ShellTypes>(
  app: ShellApp<G>,
  seats: ReadonlyArray<SeatState>,
): ShellApp<G> =>
  withShell(app, {
    seats,
    oppName: seats[0]?.name ?? null,
    oppConnected: seats[0]?.connected ?? false,
  });

/** `seats` grown to at least `n` entries, the new ones empty. */
const seatsUpTo = (seats: ReadonlyArray<SeatState>, n: number): ReadonlyArray<SeatState> =>
  Array.from({ length: Math.max(seats.length, n) }, (_, i) => seats[i] ?? EMPTY_SEAT);

/** Seat `seat` (1-based) replaced, the list grown to reach it. */
const seatAt = (
  seats: ReadonlyArray<SeatState>,
  seat: number,
  next: SeatState,
): ReadonlyArray<SeatState> =>
  seatsUpTo(seats, seat).map((current, i) => (i === seat - 1 ? next : current));

/** The guest seat for index `i` of `seats`: the array is in seat order, so the seat is the index plus one (the game's own seats past `0 | 1` come from its bag). */
const guestSeat = <G extends ShellTypes>(i: number): SeatOf<G> => (i + 1) as SeatOf<G>;

/** How many are at the table, the host counted. */
const seatedCount = (seats: ReadonlyArray<SeatState>): number =>
  1 + seats.filter((seat) => seat.connected).length;

/** The table's seats for `opts`, the host's included: the game's `opts.capacity` (its `max` without one), floored at two as the session floors it; two for a two-seat game. */
const capacityOf = <G extends ShellTypes>(opts: G['Opts'], cfg: ShellConfig<G>): number =>
  cfg.seats === undefined
    ? 2
    : Math.max(2, cfg.opts.capacity === undefined ? cfg.seats.max : cfg.opts.capacity(opts));

/**
 * The players `engine.create` deals to, from the list the shell seated (the host, then every guest
 * seat in order). The bag fixes whether the game's engine reads the pair or the list
 * (`PlayersOf<G>`), which no call site can tell, so this is the one place the list is read as the
 * game's type: a two-seat game is only ever handed two.
 */
const playersFor = <G extends ShellTypes>(players: ReadonlyArray<Player>): PlayersOf<G> =>
  players as PlayersOf<G>;

/** The seating an N-seat game's room frames carry beside the options (D3): the receiving guest's seat and one row per guest seat 1..N−1. */
const seating = object({
  you: integer(1),
  seats: arrayOf(object({ name: nullable(string), connected: boolean })),
});

/**
 * The receiver's seat and the table off a welcome or lobby frame, when the frame carries them
 * (`you` naming one of `seats`); null for a frame without (a two-seat game's, or a seating that
 * names no seat). The game's decoder has already refused a malformed seating on the wire; this
 * reads what it let through, so the shell needs no reader from the game.
 */
export const roomSeatingOf = <G extends ShellTypes>(
  frame: HostFrameOf<G>,
): Readonly<{ you: SeatOf<G>; seats: ReadonlyArray<SeatState> }> | null => {
  const decoded = seating(frame);
  if (!decoded.ok) return null;
  const { you, seats } = decoded.value;
  return you <= seats.length ? { you: you as SeatOf<G>, seats } : null;
};

/** Players at the table before the host may deal: every seat of a fixed table (`cfg.seats.fixed`: the room's capacity), else the game's `min`, or two. */
const minSeated = <G extends ShellTypes>(s: ShellState<G>, cfg: ShellConfig<G>): number =>
  cfg.seats === undefined ? 2 : cfg.seats.fixed === true ? capacityOf(s.opts, cfg) : cfg.seats.min;

/** A `send` effect to one seat of an N-seat room; a two-seat game's is the literal it was, no `seat` key (its one channel is every open one). */
const sendTo = <G extends ShellTypes>(
  frame: HostFrameOf<G>,
  seat: SeatOf<G>,
  cfg: ShellConfig<G>,
): Effect<G> => (isNSeat(cfg) ? { type: 'send', frame, seat } : { type: 'send', frame });

/** One lobby frame per connected seat, each with its own `you` (D3): on every seating change while the host waits, and at an N-seat table mid-game too (a seat down, a seat back), so every guest holds the table as the host does. */
const lobbySends = <G extends ShellTypes>(
  s: ShellState<G>,
  cfg: ShellConfig<G>,
): ReadonlyArray<Effect<G>> => {
  const seats = seatsOf(s, cfg);
  return seats.flatMap((seat, i) =>
    seat.connected
      ? [sendTo(cfg.frames.lobby(s.myName, s.opts, seats, guestSeat<G>(i)), guestSeat<G>(i), cfg)]
      : [],
  );
};

/**
 * `guestNameFor` against every name at the table (the host's and the other seats'): the wire's
 * normalisation, then ` 2`, ` 3`, … until it clashes with nobody, case-insensitively. Against the
 * host's name alone it is `guestNameFor(raw, hostName)`, ` 2` appended once.
 */
const guestNameAmong = (raw: string, taken: ReadonlyArray<string>): string => {
  const named = guestNameFor(raw, '');
  const clashes = (name: string): boolean =>
    taken.some((t) => t.toLowerCase() === name.toLowerCase());
  const free = (n: number): string =>
    clashes(`${named} ${String(n)}`) ? free(n + 1) : `${named} ${String(n)}`;
  return clashes(named) ? free(2) : named;
};

// ---- the cue memory (docs/design/dry-round-2.md F6) ---------------------------------------------

/**
 * What a game's cue machine remembers between paints so each event chimes once: the key of the
 * position it last played for (gin: a result's timestamp or the totals; backgammon: game, phase,
 * turn, log and play lengths). The shell's `cues` field holds the game's memory (`G['Cues']`: this
 * record, or one extending it, gin's adds `turnKey`); the cues themselves stay each game's
 * derivation (`cfg.table.rendered`, gin's `nextCue`), the two machines being 5/62 lines alike
 * (dry-round-2.md §2E) and sharing only this rule.
 */
export type CueMemory = Readonly<{ key: string | null }>;
/** The memory before any paint: no position played for yet, so the first view's key is fresh. Every game's `cues.initial` (gin spreads it under its `turnKey`). */
export const INITIAL_CUE_MEMORY: CueMemory = { key: null };
/** `key` against the memory: `fresh` when it names a new position; the memory keyed on it either way. */
export const fresh = (
  mem: CueMemory,
  key: string,
): Readonly<{ mem: CueMemory; fresh: boolean }> => ({ mem: { key }, fresh: mem.key !== key });

/** A game whose cue memory is the plain `CueMemory` (every game but gin): what `cuesFor` reads. */
export type CuedTypes = ShellTypes & Readonly<{ Cues: CueMemory }>;
/**
 * A game's cue machine as a table (shell-hoist.md row P): `key` names a position, so a re-sent
 * frame (the same key) plays nothing; `between` gives the cues for the change from one view to the
 * next (ui/sound.ts `cuesBetween`; flip7's reads the role and fidice's the chair off `app`);
 * `myTurn` says whether the view is my turn, for the `yourTurn` an online turn landing on my seat
 * adds. Absent, no turn is announced here (fidice's `cuesBetween` announces its own).
 */
export type CueMachine<G extends CuedTypes> = Readonly<{
  key: (view: G['View']) => string;
  between: (prev: G['View'], next: G['View'], app: ShellApp<G>) => ReadonlyArray<G['Cue']>;
  myTurn?: (view: G['View']) => boolean;
}>;
/** What one paint plays: the position's key, whether it is new since the last paint, and the cues (none for a repaint or a first view). */
export type Cued<G extends CuedTypes> = Readonly<{
  key: string;
  fresh: boolean;
  cues: ReadonlyArray<Cue<G>>;
}>;
/** The machine run once against `view` and the memory: fresh when the key is new and there is a `prev` to play the change from. */
export const cuesFor = <G extends CuedTypes>(
  app: ShellApp<G>,
  prev: G['View'] | null,
  view: G['View'],
  machine: CueMachine<G>,
): Cued<G> => {
  const key = machine.key(view);
  const fresh = prev !== null && key !== app.shell.cues.key;
  if (!fresh) return { key, fresh, cues: [] };
  const online = app.shell.role === 'host' || app.shell.role === 'guest';
  const mine = (v: G['View']): boolean => machine.myTurn?.(v) ?? false;
  const myTurnNow = online && mine(view) && !mine(prev);
  return {
    key,
    fresh,
    cues: [...machine.between(prev, view, app), ...(myTurnNow ? (['yourTurn'] as const) : [])],
  };
};
/** The `fx` effect for a cue. */
export const fx = <C extends string>(cue: C): Readonly<{ type: 'fx'; cue: C }> => ({
  type: 'fx',
  cue,
});
/**
 * The `rendered` of the shared shape (uno's, flip7's and fidice's whole): the table is the screen
 * while a view is held, the cues since `prev` play once per position and the memory is keyed on
 * it. A game with more to do at a paint runs its own over `cuesFor` (hive).
 */
export const cueStep =
  <G extends CuedTypes>(machine: CueMachine<G>) =>
  (app: ShellApp<G>, prev: G['View'] | null): Step<G> => {
    const view = app.shell.view;
    if (view === null) return pure(app);
    const { key, cues } = cuesFor(app, prev, view, machine);
    return step(
      withShell(app, { cues: { ...app.shell.cues, key }, screen: 'tableScreen' }),
      ...cues.map(fx),
    );
  };

// ---- the finished game's record ---------------------------------------------------------------

/**
 * Once per finished game (the owner, 2026-09-25): when the view first shows the game over, its
 * record (the clock, the mode, the seats' names, the game's score and winner, the outcome from
 * the user's seat) goes first into `recentGames` and out as the `recordGame` effect, and its key
 * is kept so a re-sent frame, a repaint, a re-render or the guest's late `state` frame of the
 * same game records nothing more. Every view change ends in `painted` below, so pass-and-play,
 * the host and the guest all reach here, each reading the names off its own view.
 */
const recordResult = <G extends ShellTypes>(
  app: ShellApp<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  const view = s.view;
  // My seat: 0 in pass-and-play and for the host, the frame's `you` for a guest (past 1 at an N-seat table); none at home.
  const seat = s.role === null ? null : s.mySeat;
  if (view === null || seat === null || !cfg.engine.over(view)) return pure(app);
  const key = keyOfView(cfg, view);
  if (s.recorded === key) return pure(app);
  const winner = cfg.result.winnerOf(view);
  const game: RecentGame = {
    at: ctx.now(),
    mode: s.role === 'local' ? 'local' : 'online',
    players: cfg.result.playersOf(view),
    score: cfg.result.scoreOf(view),
    winner,
    outcome: outcomeFor(seat, winner),
  };
  return step(withShell(app, { recorded: key, recentGames: appendCapped(s.recentGames, game) }), {
    type: 'recordGame',
    game,
  });
};

/** The game's `rendered` hook, then the finished game's record, then the rotation hint where it is due: every view change ends here. */
/**
 * The sheets put away wherever a shared flow resets the table (every game's `reset` returned its
 * initial table at a start, the handoff, a leave and the host lost; the #20 rule: Leave puts every
 * sheet away) and the result's dismissal with them.
 */
const SHEETS_AWAY = {
  rulesOpen: false,
  historyOpen: false,
  resultDismissed: false,
  pause: null,
} as const;

/** The pause the game's `table.pause` raises for this paint, unless one is already up (it waits for its Continue). */
const paused = <G extends ShellTypes>(
  app: ShellApp<G>,
  prev: G['View'] | null,
  cfg: ShellConfig<G>,
): ShellApp<G> => {
  const view = app.shell.view;
  if (view === null || app.shell.pause !== null || cfg.table.pause === undefined) return app;
  return withShell(app, { pause: cfg.table.pause(app, prev, view) });
};

const painted = <G extends ShellTypes>(
  app: ShellApp<G>,
  prev: G['View'] | null,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> =>
  andThen(
    andThen(cfg.table.rendered(app, prev, ctx), (a) =>
      recordResult(paused(a, prev, cfg), ctx, cfg),
    ),
    (a) => rotationHint(a, ctx, cfg),
  );

// ---- flows -------------------------------------------------------------------------------------

/**
 * `broadcast()`: my view, each connected seat's view on the wire (one `send` per seat, its own
 * redaction, D4; a two-seat game's is the one `send` it was), the table's per-view reset, saved,
 * rendered.
 */
export const broadcast = <G extends ShellTypes>(
  app: ShellApp<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const game = app.shell.game;
  if (game === null) return pure(app);
  const sends: ReadonlyArray<Effect<G>> = isNSeat(cfg)
    ? app.shell.seats.flatMap((seat, i) =>
        seat.connected
          ? [
              sendTo(
                cfg.frames.state(cfg.engine.viewFor(game, guestSeat<G>(i))),
                guestSeat<G>(i),
                cfg,
              ),
            ]
          : [],
      )
    : [{ type: 'send', frame: cfg.frames.state(cfg.engine.viewFor(game, 1)) }];
  return andThen(
    step(
      {
        shell: { ...app.shell, view: cfg.engine.viewFor(game, 0) },
        table: resetWith(app.table, 'view', cfg),
      },
      ...sends,
      { type: 'persist' },
    ),
    (a) => painted(a, app.shell.view, ctx, cfg),
  );
};

/** `actions` applied for `seat` in order, the first refusal ending the chain (backgammon's move chain; every other game's chain is one action). */
const applyAll = <G extends ShellTypes>(
  game: G['State'],
  seat: SeatOf<G>,
  actions: ReadonlyArray<G['Action']>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Result<G['State'], string> =>
  actions.reduce<Result<G['State'], string>>(
    (r, a) => (r.ok ? cfg.engine.apply(r.value, seat, a, ctx.rng, ctx.now) : r),
    ok(game),
  );

/** The App after `game` was applied: the new state, the table reset for it (`applied`), and the shell's `revealed` as given. */
const applied = <G extends ShellTypes>(
  app: ShellApp<G>,
  game: G['State'],
  revealed: SeatOf<G> | null,
  cfg: ShellConfig<G>,
): ShellApp<G> => ({
  shell: { ...app.shell, game, revealed },
  table: resetWith(app.table, 'applied', cfg),
});

/** `dispatch(seat, action)`, host only: apply, or refuse to the mover (a toast frame to the guest); then broadcast. */
export const hostDispatch = <G extends ShellTypes>(
  app: ShellApp<G>,
  seat: SeatOf<G>,
  action: G['Action'],
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const game = app.shell.game;
  if (game === null) return pure(app);
  const res = cfg.engine.apply(game, seat, action, ctx.rng, ctx.now);
  if (!res.ok)
    return seat === 0
      ? refuseWith(app, res.error, cfg)
      : step(app, sendTo(cfg.frames.toast(res.error), seat, cfg));
  return broadcast(applied(app, res.value, app.shell.revealed, cfg), ctx, cfg);
};

/**
 * `localBroadcast(initial)`: the view of whoever the game hands the phone to (`cfg.local.viewer`),
 * the curtain up when the phone must change hands, chiming unless this is the start or a reveal.
 */
export const localBroadcast = <G extends ShellTypes>(
  app: ShellApp<G>,
  initial: boolean,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const game = app.shell.game;
  if (game === null) return pure(app);
  const prev = app.shell.view;
  const { seat, curtain, effects = [] } = cfg.local.viewer(app, game);
  return andThen(
    step(
      {
        shell: { ...app.shell, view: cfg.engine.viewFor(game, seat) },
        table: { ...resetWith(app.table, 'view', cfg), curtain },
      },
      { type: 'persist' },
      ...(curtain !== null && !initial ? [{ type: 'fx', cue: 'yourTurn' } as const] : []),
      ...effects,
    ),
    (a) => painted(a, prev, ctx, cfg),
  );
};

/**
 * `actAll(actions)` by role (docs/design/dry-review-2026-10.md §2.6: the switch every game's
 * `tableIntent` carried): on a pass-and-play phone the chain is applied for the seat that would
 * lift the curtain (`cfg.local.revealer`: the actor, or whoever holds the phone between rounds),
 * the reveal cleared when a new game began (`cfg.local.newGame`), then `localBroadcast`; the host
 * applies it for seat 0 and broadcasts once; a connected guest sends one `action` frame per
 * action, in order (the host applies them one by one); a guest without its host, or no role at
 * all, hears `NOT_CONNECTED_MSG`. A refusal is the table's `refuse` hook (`refuseWith`). Nothing
 * moves while a pause waits for its Continue (`ShellState.pause`); what else a game guards before
 * the shell acts stays its own (flip7's guest between rounds, gin's draw stage).
 */
export const actAll = <G extends ShellTypes>(
  app: ShellApp<G>,
  actions: ReadonlyArray<G['Action']>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const game = app.shell.game;
  if (app.shell.pause !== null) return pure(app);
  switch (app.shell.role) {
    case 'local': {
      if (game === null) return pure(app);
      const res = applyAll(game, cfg.local.revealer(game).seat, actions, ctx, cfg);
      if (!res.ok) return refuseWith(app, res.error, cfg);
      const fresh = cfg.local.newGame?.(game, res.value) ?? false;
      return localBroadcast(
        applied(app, res.value, fresh ? null : app.shell.revealed, cfg),
        false,
        ctx,
        cfg,
      );
    }
    case 'host': {
      if (game === null) return pure(app);
      const res = applyAll(game, 0, actions, ctx, cfg);
      if (!res.ok) return refuseWith(app, res.error, cfg);
      return broadcast(applied(app, res.value, app.shell.revealed, cfg), ctx, cfg);
    }
    case 'guest':
    case null:
      // A guest's channel is open exactly while the host counts as connected; no role has no channel.
      return app.shell.role === 'guest' && app.shell.oppConnected
        ? step(
            app,
            ...actions.map((a): Effect<G> => ({ type: 'send', frame: cfg.frames.action(a) })),
          )
        : refuseWith(app, NOT_CONNECTED_MSG, cfg);
  }
};

/** `act(action)`: `actAll` over the one action every game but backgammon commits at a time. */
export const act = <G extends ShellTypes>(
  app: ShellApp<G>,
  action: G['Action'],
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => actAll(app, [action], ctx, cfg);

/** `again/click` at a guest: the host deals the next game; the toast names it. */
export const hostDealsAgainMsg = (hostName: string | null): string =>
  `Waiting for ${hostName ?? 'the host'} to deal again.`;

/** A seat's player id as the shell deals it: `localSeats`' `p1`, `p2`… on a phone, `host/deal`'s `host`, `guest`, `guest2`… in a room. */
const seatId = (role: Role, i: number): string =>
  role === 'local'
    ? `p${String(i + 1)}`
    : i === 0
      ? 'host'
      : i === 1
        ? 'guest'
        : `guest${String(i + 1)}`;

/**
 * `again/click` (docs/design/dry-review-2026-10.md §2.6 line 270: flip7's `replay`, uno's and
 * hive's engine `again` dispatched from the result sheet): once the game is over, the same seats
 * (the names off my view, `cfg.result.playersOf`) and the room's terms through `engine.create`, as
 * `host/deal` deals (the sheets away, the table reset for a deal); on a phone the reveal is
 * cleared too, so the curtain names the new game's starter; the host broadcasts; a guest is told
 * the host deals (`hostDealsAgainMsg`). Nothing before the game is over. Exported for the game
 * whose one primary is Next round until the game's end (flip7's `next/click`).
 */
export const again = <G extends ShellTypes>(
  app: ShellApp<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  const view = s.view;
  if (view === null || s.role === null || !cfg.engine.over(view)) return pure(app);
  if (s.role === 'guest') return refuseWith(app, hostDealsAgainMsg(s.oppName), cfg);
  const role = s.role;
  const players = cfg.result.playersOf(view).map((name, i) => ({ id: seatId(role, i), name }));
  const game = cfg.engine.create(playersFor<G>(players), s.opts, ctx.rng, ctx.now);
  const dealt: ShellApp<G> = {
    shell: { ...s, game, ...SHEETS_AWAY, ...(role === 'local' ? { revealed: null } : {}) },
    table: resetWith(app.table, 'deal', cfg),
  };
  return role === 'local' ? localBroadcast(dealt, false, ctx, cfg) : broadcast(dealt, ctx, cfg);
};

/** The shell seated for pass-and-play with `game` and the table reset for it: what `startLocal` broadcasts (gin's sandbox seats its hand-made melds in between). */
export const localSeated = <G extends ShellTypes>(
  app: ShellApp<G>,
  game: G['State'],
  cfg: ShellConfig<G>,
): ShellApp<G> => {
  // Every seat is played from this device: the names off seat 0's view (the view has them for every role).
  const localNames = cfg.result.playersOf(cfg.engine.viewFor(game, 0));
  return {
    shell: {
      ...app.shell,
      role: 'local',
      code: null,
      oppConnected: true,
      game,
      revealed: null,
      mySeat: 0,
      localNames,
      localSeats: localNames.map((_, i) => i as SeatOf<G>),
      gateDismissed: false,
      rotationHintShown: false,
      ...SHEETS_AWAY,
    },
    table: resetWith(app.table, 'startLocal', cfg),
  };
};

/** `startLocal(game)`: pass-and-play, no Peer; the wake lock is held, and the Android lock where the device can (`lockSideways`: the Start is a tap); the curtain names the starter. */
export const startLocal = <G extends ShellTypes>(
  app: ShellApp<G>,
  game: G['State'],
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> =>
  andThen(
    andThen(step(localSeated(app, game, cfg), { type: 'wakeLock', hold: true }), (a) =>
      lockSideways(a, ctx, cfg),
    ),
    (a) => localBroadcast(a, true, ctx, cfg),
  );

/**
 * `position/load` (docs/design/dry-round-2.md F5; backgammon's `sandboxLoad` as it stood, over the
 * config): the pass-and-play game's engine state replaced by the decoded one, the table reset as
 * a start resets it, and the phone left with whoever must act, the seat the curtain would lift for
 * (`cfg.local.revealer`, so no curtain comes up and no adapter is added for the one seat). Refused
 * with a toast in any other role, and for a state the decoder refuses (`formatError` names the
 * path). What sends the intent is the game's hook (`__backgammon.setup`); nothing here knows it.
 */
const loadPosition = <G extends ShellTypes>(
  app: ShellApp<G>,
  raw: unknown,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  if (app.shell.role !== 'local' || app.shell.game === null)
    return step(app, toast(SANDBOX_LOCAL_ONLY_MSG));
  const decoded = cfg.engine.decodeState(raw);
  if (!decoded.ok) return step(app, toast(badPositionMsg(formatError(decoded.error))));
  const game = decoded.value;
  return localBroadcast(
    {
      shell: {
        ...app.shell,
        game,
        revealed: cfg.local.revealer(game).seat,
        gateDismissed: false,
        rotationHintShown: false,
        ...SHEETS_AWAY,
      },
      table: resetWith(app.table, 'startLocal', cfg),
    },
    true,
    ctx,
    cfg,
  );
};

/** `startHost(resumeCode)` up to the network: the session is the `startHost` effect. */
const startHost = <G extends ShellTypes>(
  app: ShellApp<G>,
  resumeCode: string | null,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const code = resumeCode ?? randomCode(cfg.id, ctx.rng);
  const attempt = app.shell.netAttempt + 1;
  // The room's seats, `capacity − 1` of them: as the caller left them (empty for a fresh room; named
  // and disconnected for a resumed or handed-off one), grown to the capacity the terms name.
  const capacity = capacityOf(app.shell.opts, cfg);
  const seats = seatsUpTo(seatsOf(app.shell, cfg), capacity - 1).slice(0, capacity - 1);
  return andThen(
    showScreen(
      withHostStatus(
        withSeats(
          withShell(app, {
            role: 'host',
            code,
            netAttempt: attempt,
            startGameVisible: false,
            openedAt: ctx.now(),
            mySeat: 0,
          }),
          seats,
        ),
        copyOf(cfg).opening,
      ),
      'hostWaitScreen',
    ),
    (a) =>
      step(a, {
        type: 'startHost',
        code,
        attempt,
        resume: resumeCode !== null,
        // An N-seat room tells the session its capacity and its own open status (D8), and a resumed
        // or handed-off room the names its seats held, so the session seats each guest back by name
        // whatever order they return in (D6); a two-seat game's effect is the literal it was.
        ...(cfg.seats === undefined
          ? {}
          : {
              capacity,
              ...(cfg.copy.waiting === undefined ? {} : { waiting: cfg.copy.waiting(capacity) }),
              ...(seats.some((seat) => seat.name !== null)
                ? { names: seats.map((seat) => seat.name) }
                : {}),
            }),
      }),
  );
};

/** `startGuest(code)` up to the network: the session is the `startGuest` effect. */
const startGuest = <G extends ShellTypes>(
  app: ShellApp<G>,
  code: string,
  cfg: ShellConfig<G>,
): Step<G> => {
  const attempt = app.shell.netAttempt + 1;
  return andThen(
    showScreen(
      withGuestStatus(
        // Seat 1 and no seated name until the host's welcome names my seat; the seat list is the host's to send.
        withShell(app, {
          role: 'guest',
          code,
          netAttempt: attempt,
          mySeat: 1,
          seats: [],
          seatedName: null,
        }),
        copyOf(cfg).connecting(code),
      ),
      'guestWaitScreen',
    ),
    (a) => step(a, { type: 'startGuest', code, attempt }),
  );
};

/**
 * `onGuestGone()` after `oppConnected` was cleared. During a handoff nobody has joined yet (the
 * game is there, but the room still waits for the invite to be followed), so a channel that closed
 * or failed before its join leaves the wait screen saying to send the invite, with no toast.
 */
const guestGone = <G extends ShellTypes>(
  app: ShellApp<G>,
  seat: SeatOf<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  if (s.handoff) return pure(withHostStatus(app, copyOf(cfg).handoff(s.code ?? '', s.oppName)));
  if (s.game !== null && s.view !== null && !cfg.engine.over(s.view))
    return andThen(painted(app, s.view, ctx, cfg), (a) => {
      const name = seatsOf(a.shell, cfg)[seat - 1]?.name ?? null;
      const text =
        cfg.copy.guestGone === undefined
          ? guestGoneMsg(name, a.shell.code)
          : cfg.copy.guestGone(name, a.shell.code, seat);
      // At an N-seat table every seat still up learns which one is down (the lobby carries the
      // table; the views carry no channel state), so a guest can pause with the host.
      return step(a, toast(text, GONE_TOAST_MS), ...(isNSeat(cfg) ? lobbySends(a.shell, cfg) : []));
    });
  if (s.game === null) {
    if (!isNSeat(cfg))
      return pure(withShell(withHostStatus(app, OPPONENT_LEFT_MSG), { startGameVisible: false }));
    // The seat reads empty again, Start follows the count, the others learn the new lobby.
    const name = s.seats[seat - 1]?.name ?? null;
    const left = withSeats(app, seatAt(s.seats, seat, EMPTY_SEAT));
    const seated = seatedCount(left.shell.seats);
    const capacity = left.shell.seats.length + 1;
    const text =
      cfg.copy.seatLeft === undefined
        ? OPPONENT_LEFT_MSG
        : cfg.copy.seatLeft(name, seat, seated, capacity);
    return step(
      withShell(withHostStatus(left, text), {
        startGameVisible: seated >= minSeated(left.shell, cfg),
      }),
      ...lobbySends(left.shell, cfg),
    );
  }
  return pure(app);
};

/** `onGuestMsg(conn, msg)` for a decoded frame on `seat`'s channel (1 for a two-seat game). */
const hostFrame = <G extends ShellTypes>(
  app: ShellApp<G>,
  frame: GuestFrameOf<G>,
  seat: SeatOf<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  switch (frame.t) {
    case 'join': {
      // The name against the host's and the other seats' (the seat's own last name is not a clash: a rejoin keeps it).
      const seats = seatsOf(s, cfg);
      const others = seats.flatMap((other, i) =>
        i === seat - 1 || other.name === null ? [] : [other.name],
      );
      const name = guestNameAmong(frame.name, [s.myName, ...others]);
      const joined = withShell(withSeats(app, seatAt(seats, seat, { name, connected: true })), {
        handoff: false,
      });
      // The wait screen names the newcomer under its seated name on both paths: on a rejoin into a
      // game the screen sits hidden under the table, but a stale "send Ethan the invite…" or
      // "waiting for Xyz to rejoin…" must not be what a later look finds (repro-names N3).
      const seated = seatedCount(joined.shell.seats);
      const capacity = joined.shell.seats.length + 1;
      const names = joined.shell.seats.flatMap((x) =>
        x.connected && x.name !== null ? [x.name] : [],
      );
      const text =
        cfg.copy.joined === undefined
          ? joinedMsg(name)
          : cfg.copy.joined(name, names, capacity - seated);
      const connected = withHostStatus(joined, text);
      if (s.game !== null) {
        // Rejoin: keep the seat, refresh the name. At an N-seat table the lobby goes round first
        // (the returner learns its seat, `you`, which the session may have moved by name since its
        // welcome; every other seat learns the table is whole again), then the views.
        const renamed = withShell(connected, {
          game: cfg.engine.renameGuest(s.game, name, seat),
        });
        return andThen(
          step(renamed, ...(isNSeat(cfg) ? lobbySends(renamed.shell, cfg) : [])),
          (a) => broadcast(a, ctx, cfg),
        );
      }
      return step(
        withShell(connected, {
          startGameVisible: seated >= minSeated(connected.shell, cfg),
        }),
        ...lobbySends(connected.shell, cfg),
      );
    }
    case 'action':
      return s.game === null ? pure(app) : hostDispatch(app, seat, frame.action, ctx, cfg);
    case EPHEMERAL_TAG:
      return cfg.table.ephemeral?.(app, frame, seat, ctx) ?? pure(app);
  }
};

/** `onHostMsg(msg)` for a decoded frame. */
const guestFrame = <G extends ShellTypes>(
  app: ShellApp<G>,
  frame: HostFrameOf<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  switch (frame.t) {
    case 'welcome':
    case 'lobby': {
      const opts = cfg.opts.pick(frame);
      // An N-seat room's frame names my seat and lists the others (D3); a two-seat game's carries neither and says two of two.
      const room = cfg.seats === undefined ? null : roomSeatingOf<G>(frame);
      const seated = room === null ? 2 : seatedCount(room.seats);
      const capacity = room === null ? 2 : room.seats.length + 1;
      // What the host calls me. A frame with no seating is a room of exactly two (a two-seat
      // game's, and an N-seat game's at two seats, where its protocol leaves `seats`/`you` off):
      // the host's rule mirrored (`guestNameFor` against its name, the one other name at the
      // table, so exact by construction). At a frame with a seating, only a `lobby` names my row,
      // never a `welcome`: the welcome goes out at channel open, before my join reaches the host,
      // so its row for my seat can be a vacated occupant's kept name (mid-game `host/guestGone`
      // keeps `{ name, connected: false }` for the rejoin) or a resumed room's saved name; the
      // lobby that answers my join names me.
      const seatedName =
        room === null
          ? guestNameFor(app.shell.myName, frame.hostName)
          : frame.t === 'lobby'
            ? (room.seats[room.you - 1]?.name ?? app.shell.seatedName)
            : app.shell.seatedName;
      return pure(
        withGuestStatus(
          withShell(app, {
            oppName: frame.hostName,
            opts,
            seatedName,
            ...(room === null ? {} : { mySeat: room.you, seats: room.seats }),
          }),
          cfg.copy.hostRoom(frame.hostName, opts, seated, capacity),
        ),
      );
    }
    case 'full':
      return pure(withGuestStatus(app, cfg.copy.roomFull ?? ROOM_FULL_MSG));
    case 'toast':
      // The host refused the guest's move.
      return refuseWith(app, frame.msg, cfg);
    case 'state': {
      // My seat's name as the host dealt it: the word every painter shows, whatever rule made it.
      // The view's row for my seat: `result.seatName` where a game's players are chairs, not
      // seats; else the seat's index (the view names every seat: the game's decoder refused any
      // other shape on the wire). A view with no row for me keeps the last word.
      const named =
        cfg.result.seatName === undefined
          ? cfg.result.playersOf(frame.view)[app.shell.mySeat]
          : cfg.result.seatName(frame.view, app.shell.mySeat);
      return painted(
        {
          shell: {
            ...app.shell,
            view: frame.view,
            oppConnected: true,
            seatedName: named ?? app.shell.seatedName,
          },
          table: resetWith(app.table, 'frame', cfg),
        },
        app.shell.view,
        ctx,
        cfg,
      );
    }
    case EPHEMERAL_TAG:
      return cfg.table.ephemeral?.(app, frame, 0, ctx) ?? pure(app);
  }
};

// ---- home, resume, leave ---------------------------------------------------------------------

/**
 * The resume box `initHome` shows for a save, or null (a finished game is not offered); the game's
 * own offers come first through `cfg.home.resume`. A host save with no game is the waiting room
 * this device opened (lobby-resume.md D3): offered under its code, with the save's stamp, so
 * `resume/auto` can tell a moment ago from last week.
 */
export const resumeFor = <G extends ShellTypes>(
  save: Save<G> | null,
  cfg: ShellConfig<G>,
): ShellResume<G> | null => {
  if (save === null) return null;
  switch (save.role) {
    case 'local':
      return cfg.engine.finished(save.game) ? null : { kind: 'local', game: save.game };
    case 'host':
      return save.game === null || !cfg.engine.finished(save.game)
        ? {
            kind: 'host',
            code: save.code,
            myName: save.myName,
            ...cfg.opts.pick(save),
            game: save.game,
            oppName: save.oppName,
            ...(save.seatNames === undefined ? {} : { seatNames: save.seatNames }),
            handoff: save.handoff === true,
            at: save.at ?? null,
          }
        : null;
    case 'guest':
      return { kind: 'guest', code: save.code, myName: save.myName };
  }
};

const SHELL_RESUME_KINDS: ReadonlySet<string> = new Set(['local', 'host', 'guest']);
const isShellResume = <G extends ShellTypes>(offer: Resume<G>): offer is ShellResume<G> =>
  SHELL_RESUME_KINDS.has(offer.kind);

/** The room this device hosts, as the home screen offers it: the host offer, or null. */
const hostOffer = <G extends ShellTypes>(s: ShellState<G>): HostResume<G> | null =>
  s.resume !== null && isShellResume(s.resume) && s.resume.kind === 'host' ? s.resume : null;

/**
 * `resume/auto`'s rule (lobby-resume.md D4): the offer is this device's waiting room (no game),
 * stamped, and opened within WAITING_RESUME_MS of now. A mid-game room, an unstamped save (from
 * before the stamp) and an old lobby wait for their tap.
 */
const resumesItself = <G extends ShellTypes>(offer: HostResume<G>, now: number): boolean =>
  offer.game === null && offer.at !== null && now - offer.at <= WAITING_RESUME_MS;

/** `setHomeTab(tab, opts)`. */
const setHomeTab = <G extends ShellTypes>(
  app: ShellApp<G>,
  tab: string,
  persist: boolean,
  cfg: ShellConfig<G>,
): Step<G> => {
  const tabs = tabsOf(cfg);
  const known = tabs.list.find((t) => t === tab) ?? tabs.default;
  return step(
    withShell(app, { homeTab: known }),
    ...(persist ? [{ type: 'writeHomeTab', tab: known } as const] : []),
  );
};

/**
 * The room's terms remembered after a step that set them (`opts/set`, `host/click`,
 * `local/click`): the `writeOpts` effect after the step's own, for a game whose prefs hold the
 * terms; a game without the pref (gin, hive) gets the step as it was.
 */
const rememberOpts = <G extends ShellTypes>(s: Step<G>, cfg: ShellConfig<G>): Step<G> =>
  cfg.prefs.opts === undefined
    ? s
    : andThen(s, (a) => step(a, { type: 'writeOpts', opts: a.shell.opts }));

/**
 * `initHome()` over a storage snapshot: the saved names, or the game's defaults where none is
 * saved (`localNamesOf`, marked `default` so the page clears them on the first tap), go into the
 * name inputs (effects, so the paint never fights the player's typing; the shell's state keeps
 * only what was saved or typed), the game's own part into the App (`cfg.home.apply`), the tab
 * applied without persisting, then the resume offer.
 */
const initHome = <G extends ShellTypes>(
  app: ShellApp<G>,
  home: HomeSnapshot<G>,
  cfg: ShellConfig<G>,
): Step<G> =>
  andThen(showScreen(app, 'homeScreen'), (a) =>
    andThen(
      andThen(
        step(
          homeOf(cfg).apply(
            withShell(a, {
              savedName: home.name,
              p1Name: home.name ?? '',
              p2Name: home.p2Name ?? '',
              nameTouched: home.name !== null ? true : a.shell.nameTouched,
              homeTab: home.homeTab,
              playMode: home.playMode,
              soundFont: home.soundFont,
              flipForFar: home.flipTable,
              recentGames: home.recentGames,
              opts: home.opts ?? a.shell.opts,
              seatNames: home.seatNames ?? a.shell.seatNames,
            }),
            home,
          ),
          home.name === null
            ? { type: 'fillName', name: localNameFor(localNamesOf(cfg), 0), default: true }
            : { type: 'fillName', name: home.name },
          home.p2Name === null
            ? { type: 'fillP2Name', name: localNameFor(localNamesOf(cfg), 1), default: true }
            : { type: 'fillP2Name', name: home.p2Name },
        ),
        (b) => setHomeTab(b, home.homeTab, false, cfg),
      ),
      (b) => pure(withShell(b, { resume: resumeOffer(home, cfg) })),
    ),
  );

/** The resume box for a snapshot: the game's order (`home.resume`), else the shell's three offers off the save. */
const resumeOffer = <G extends ShellTypes>(
  home: HomeSnapshot<G>,
  cfg: ShellConfig<G>,
): Resume<G> | null => {
  const own = homeOf(cfg).resume;
  return own === undefined ? resumeFor(home.save, cfg) : own(home);
};

/** `#resumeBtn` for each offer; one the shell does not know is the game's (`cfg.home.resumeExtra`). */
const resume = <G extends ShellTypes>(
  app: ShellApp<G>,
  offer: Resume<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  if (!isShellResume(offer)) {
    const extra = homeOf(cfg).resumeExtra;
    return extra === undefined ? pure(app) : extra(app, offer, ctx);
  }
  switch (offer.kind) {
    case 'local':
      return startLocal(app, offer.game, ctx, cfg);
    case 'host':
      return startHost(
        withSeats(
          withShell(app, {
            myName: offer.myName,
            opts: cfg.opts.pick(offer),
            game: offer.game,
            // A waiting room has no game and no view yet: it reopens as it was, every seat free.
            view: offer.game === null ? null : cfg.engine.viewFor(offer.game, 0),
            // A handoff nobody joined resumes as one, under the code the invite already carries.
            handoff: offer.handoff,
          }),
          // Every seat named as saved and disconnected; each guest lands back in its own by name (D6).
          (offer.seatNames ?? [offer.oppName]).map((name) => ({ name, connected: false })),
        ),
        offer.code,
        ctx,
        cfg,
      );
    case 'guest':
      return startGuest(withShell(app, { myName: offer.myName }), offer.code, cfg);
  }
};

/**
 * The pass-and-play game a handoff would host, when it can go on as a two-seat room: the game in
 * play, else the home's pass-and-play offer; null when there is neither, or the table seats more
 * than two. A game with no seat table seats two; an N-seat game's terms (`opts.ofGame`, through
 * `capacityOf`) must say two, and the game may add its own rule (`engine.handoffable`).
 */
const handoffGameOf = <G extends ShellTypes>(
  s: ShellState<G>,
  cfg: ShellConfig<G>,
): G['State'] | null => {
  const game =
    s.role === 'local' && s.game !== null
      ? s.game
      : s.resume !== null && isShellResume(s.resume) && s.resume.kind === 'local'
        ? s.resume.game
        : null;
  return game !== null &&
    capacityOf(cfg.opts.ofGame(game), cfg) === 2 &&
    (cfg.engine.handoffable?.(game) ?? true)
    ? game
    : null;
};

/** `handoff/click` would open a room: a pass-and-play game or offer stands and seats two (`handoffGameOf`). */
export const handoffable = <G extends ShellTypes>(s: ShellState<G>, cfg: ShellConfig<G>): boolean =>
  handoffGameOf(s, cfg) !== null;

/**
 * `#handoffBtn`'s tooltip (shellPaint.ts `paintHandoff`): who hosts and who joins, for the
 * pass-and-play game in play that can go on as a two-seat room; null hides the button (nothing in
 * play, a room online already, a table of more than two). The home's offer is the resume box's
 * (web/shared/lib/name.ts `resumeLabel`), not this button's.
 */
export const handoffLabelOf = <G extends ShellTypes>(
  s: ShellState<G>,
  cfg: ShellConfig<G>,
): string | null =>
  s.role === 'local' && s.game !== null && handoffable(s, cfg)
    ? handoffLabel(cfg.engine.names(s.game))
    : null;

/**
 * `#handoffBtn`: the pass-and-play game goes on as a hosted room with a fresh code. Seat 0 keeps
 * this device as the host; seat 1 joins from its own through the invite, and the host's join
 * handler takes it as a rejoin (the seat is kept, the name refreshed, the game broadcast). The
 * pass-and-play marks (the curtain, the revealed seat, the table's taps) are cleared as a leave
 * clears them.
 */
const handoff = <G extends ShellTypes>(
  app: ShellApp<G>,
  game: G['State'],
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const [host, guest] = cfg.engine.names(game);
  return startHost(
    withSeats(
      {
        shell: {
          ...app.shell,
          myName: host,
          opts: cfg.opts.ofGame(game),
          game,
          view: cfg.engine.viewFor(game, 0),
          revealed: null,
          handoff: true,
          localNames: [],
          localSeats: [],
          gateDismissed: false,
          rotationHintShown: false,
          ...SHEETS_AWAY,
        },
        table: resetWith(app.table, 'handoff', cfg),
      },
      [{ name: guest, connected: false }],
    ),
    null,
    ctx,
    cfg,
  );
};

/** `leaveGame()` after the confirm and the network close: the reset (the cue memory too, and the gate's dismissal: both belonged to the game left), then home. */
const leaveFinish = <G extends ShellTypes>(app: ShellApp<G>, cfg: ShellConfig<G>): Step<G> =>
  step(
    {
      shell: {
        ...app.shell,
        netAttempt: app.shell.netAttempt + 1,
        role: null,
        game: null,
        view: null,
        oppConnected: false,
        code: null,
        revealed: null,
        handoff: false,
        cues: initialCuesOf(cfg),
        recorded: null,
        // No room and no table: the legacy `oppName` lingers until the next room names one.
        seats: [],
        mySeat: 0,
        seatedName: null,
        localNames: [],
        localSeats: [],
        gateDismissed: false,
        rotationHintShown: false,
        // `leave/confirmed` dropped the lock: the page is upright-free again until the next tap.
        orientationLocked: false,
        // The shell's own sheets, open at the leave, go with the table: the home has no sheet
        // (shellPaint.ts `paintScreen` puts every overlay away there; this keeps the next paint
        // from bringing the rules back over it).
        ...SHEETS_AWAY,
      },
      table: resetWith(app.table, 'leave', cfg),
    },
    { type: 'clearSave' },
    { type: 'initHome' },
  );

/**
 * `#cancelHostBtn` / `#cancelGuestBtn` after the Peer is destroyed. A handed-off game nobody
 * joined goes back to pass-and-play instead of being cleared with the room.
 */
const cancelFinish = <G extends ShellTypes>(app: ShellApp<G>): Step<G> =>
  step(
    withShell(app, {
      role: null,
      netAttempt: app.shell.netAttempt + 1,
      handoff: false,
      seats: [],
      seatedName: null,
      // `cancel` dropped the lock a Sit down or a handoff took.
      orientationLocked: false,
      // The table's marks go with the room: a guest whose host was lost after "Play upright"
      // takes no stale choice (and no spent hint) home to the next table.
      gateDismissed: false,
      rotationHintShown: false,
    }),
    app.shell.handoff && app.shell.game !== null
      ? { type: 'saveLocal', game: app.shell.game }
      : { type: 'clearSave' },
    { type: 'initHome' },
  );

// ---- the reducer -------------------------------------------------------------------------------

/** One shell intent against the App; the game's `reduce` sends every `isShellIntent` here. */
export const reduceShell = <G extends ShellTypes>(
  app: ShellApp<G>,
  intent: ShellIntent<G>,
  ctx: Ctx,
  cfg: ShellConfig<G>,
): Step<G> => {
  const s = app.shell;
  switch (intent.type) {
    // ---- home ----
    case 'home/init':
      return initHome(app, intent.home, cfg);
    // A name typed into any of its inputs is remembered trimmed and shown, as typed, in the
    // others; the fill writes only inputs whose value differs, so the one being typed in is left alone.
    case 'name/typed':
      return step(
        withShell(app, { nameTouched: true, p1Name: intent.value }),
        { type: 'rememberName', name: intent.value.trim() },
        { type: 'fillName', name: intent.value },
      );
    case 'p1name/typed':
      return step(
        withShell(app, { p1Name: intent.value }),
        { type: 'rememberName', name: intent.value.trim() },
        { type: 'fillName', name: intent.value },
      );
    case 'p2name/typed':
      return step(
        withShell(app, { p2Name: intent.value }),
        { type: 'rememberP2Name', name: intent.value.trim() },
        { type: 'fillP2Name', name: intent.value },
      );
    // The input is the only place the name shows, so no fill: the paint reads `seatNames` ('' kept empty).
    case 'seatName/typed':
      return step(
        withShell(app, {
          seatNames: s.seatNames.map((name, i) => (i === intent.seat - 2 ? intent.value : name)),
        }),
        { type: 'rememberSeatName', seat: intent.seat, name: intent.value.trim() },
      );
    case 'tab/set':
      return setHomeTab(app, intent.tab, intent.persist !== false, cfg);
    case 'rules/show': {
      // On the home screen the Rules tab is the rules; anywhere else (the table, a waiting room,
      // the end screen) the overlay is, and its own copy of the list is the one to scroll.
      const home = s.screen === 'homeScreen';
      const shown = home
        ? setHomeTab(app, 'rules', true, cfg)
        : pure(withShell(app, { rulesOpen: true }));
      return andThen(shown, (a) =>
        step(a, {
          type: 'revealRule',
          slot: home ? 'rulesList' : 'rulesOverlayList',
          rule: intent.rule,
        }),
      );
    }
    case 'mode/set': {
      const parsed = (cfg.modes?.parse ?? defaultModeParse)(intent.mode, s);
      if (parsed === null) return pure(app);
      return step(
        withShell(app, { playMode: parsed.shown }),
        ...(parsed.stored === null
          ? []
          : [{ type: 'writePlayMode', mode: parsed.stored } as const]),
      );
    }
    case 'opts/set':
      return rememberOpts(pure(withShell(app, { opts: cfg.opts.parse(intent.raw, s.opts) })), cfg);
    case 'host/click':
      return rememberOpts(
        startHost(
          withSeats(
            withShell(app, {
              myName: nameOr(intent.name, defaultNameOf(cfg)),
              opts: cfg.opts.parse(intent, s.opts),
              game: null,
              view: null,
            }),
            // A fresh room: no seat named (`startHost` opens the capacity the terms name).
            [],
          ),
          null,
          ctx,
          cfg,
        ),
        cfg,
      );
    case 'join/click': {
      const code = validateCode(cfg.id, intent.code);
      if (!code.ok) return step(app, toast(code.error));
      // The Sit down is a tap: the Android lock rides it (`lockSideways`), before the room.
      return andThen(lockSideways(app, ctx, cfg), (a) =>
        startGuest(withShell(a, { myName: guestName(intent.name, cfg) }), code.value, cfg),
      );
    }
    case 'local/click': {
      // Every seat the terms hold, from the inputs (the third on as the click carries them, else as
      // remembered) under the `localSeats` rule; a two-seat game's is `localPlayers(p1, p2)`.
      const opts = cfg.opts.parse(intent, s.opts);
      const extra = s.seatNames.map((name, i) => intent.names?.[i] ?? name ?? '');
      const raws = [intent.p1, intent.p2, ...extra].slice(0, capacityOf(opts, cfg));
      const game = cfg.engine.create(
        playersFor<G>(localSeats(raws, localNamesOf(cfg))),
        opts,
        ctx.rng,
        ctx.now,
      );
      return rememberOpts(startLocal(withShell(app, { opts }), game, ctx, cfg), cfg);
    }
    case 'resume/click': {
      // Resume is a tap: the Android lock re-enters on it (a reload left fullscreen behind).
      const offer = s.resume;
      return offer === null
        ? pure(app)
        : andThen(lockSideways(app, ctx, cfg), (a) => resume(a, offer, ctx, cfg));
    }
    case 'resume/auto': {
      if (s.role !== null) return pure(app);
      const own = hostOffer(s);
      return own !== null && resumesItself(own, ctx.now()) ? resume(app, own, ctx, cfg) : pure(app);
    }
    case 'handoff/click': {
      // The home screen's offer, or the game in play on the pass-and-play curtain, when it seats two.
      const game = handoffGameOf(s, cfg);
      return game === null ? pure(app) : handoff(app, game, ctx, cfg);
    }
    case 'name/rename': {
      // Only a guest whose channel to the host is open: a second join on the same channel is the
      // host's re-seat (`hostFrame` `join`, G5), before the deal or into a game (a rejoin's
      // rename, `engine.renameGuest`). The seated name is the two-seat mirror at once (`guestFrame`
      // `welcome`: a host at capacity 2 answers a repeat join with its status alone, so nothing
      // else would tell the guest); an N-seat host's lobby names my row and overwrites it. What was
      // typed is remembered as `name/typed` remembers it, so the next visit joins under it.
      if (s.role !== 'guest' || !s.oppConnected) return pure(app);
      const myName = guestName(intent.name, cfg);
      return step(
        withShell(app, {
          myName,
          seatedName: s.oppName === null ? s.seatedName : guestNameFor(myName, s.oppName),
        }),
        { type: 'send', frame: cfg.frames.join(myName) },
        { type: 'rememberName', name: intent.name.trim() },
      );
    }
    case 'cancel':
      // The Android lock a Sit down or a handoff took goes with the room (`unlockSideways`).
      return step(
        app,
        ...unlockSideways(ctx, cfg),
        { type: 'closeNet' },
        { type: 'then', intent: { type: 'cancel/finish' } },
      );
    case 'cancel/finish':
      return cancelFinish(app);
    case 'screen/show':
      return showScreen(app, intent.screen);
    case 'submenu/press':
      return step(withShell(app, { longPressed: false }), {
        type: 'startTimer',
        id: 'longPress',
        ms: LONG_PRESS_MS,
        then: { type: 'submenu/longPress' },
      });
    case 'submenu/release':
      return step(app, { type: 'cancelTimer', id: 'longPress' });
    case 'submenu/longPress':
      return step(withShell(app, { longPressed: true, submenuOpen: true }), tap);
    case 'tab/playClick':
      // The long press already opened the submenu; the click that follows must not switch tabs.
      return s.longPressed
        ? pure(withShell(app, { longPressed: false }))
        : setHomeTab(withShell(app, { submenuOpen: false }), 'play', true, cfg);
    case 'submenu/pick':
      return andThen(reduceShell(app, { type: 'mode/set', mode: intent.mode }, ctx, cfg), (a) =>
        setHomeTab(withShell(a, { submenuOpen: false }), 'play', true, cfg),
      );
    case 'submenu/dismiss':
      return pure(withShell(app, { submenuOpen: false }));
    case 'code/typed': {
      // A keyboard suggestion that swapped earlier letters arrives as a replacement: keep the last good code.
      const value =
        intent.inputType === 'insertReplacementText'
          ? s.codeDraft
          : sanitiseCode(cfg.id, intent.value);
      return step(withShell(app, { codeDraft: value }), { type: 'setCode', value });
    }
    case 'join/link': {
      // The invite link is the tap on `#joinBtn` (the owner, 2026-09-25: "it should immediately act
      // as if you have clicked that already"): the form is filled first, as it was, so a refusal or
      // a cancel leaves the code in the input on the Play tab in online mode (the mode is shown, not
      // stored), then `join/click` with that code and the remembered name, or none: the link's
      // guest never saw the box, so the prefill it shows is not its word, and the click's rule
      // falls back to the guest's default (never the local one). A bad code is the click's toast
      // over the filled form; a link followed while seated changes nothing.
      if (s.role !== null) return pure(app);
      const code = sanitiseCode(cfg.id, intent.code);
      // The device's own invite (the owner, 2026-09-25: "when a host device clicks the JOIN code
      // it should resume hosting"): the room it hosts, waiting or mid-game, comes back as the
      // Resume tap would bring it, and nobody joins.
      const own = hostOffer(s);
      if (own !== null && own.code === code) return resume(app, own, ctx, cfg);
      return andThen(
        andThen(
          setHomeTab(withShell(app, { playMode: 'online', codeDraft: code }), 'play', false, cfg),
          (a) => step(a, { type: 'setCode', value: code }),
        ),
        // The link is no tap (the boot runs it): the click's Android lock is not asked for, since
        // `requestFullscreen` without activation would only fail; the next tap takes it.
        (a) =>
          reduceShell(
            a,
            { type: 'join/click', name: s.p1Name, code },
            { ...ctx, canLock: false },
            cfg,
          ),
      );
    }
    case 'sound/toggle':
      return step(app, { type: 'toggleSound' });
    case 'soundFont/set':
      return step(withShell(app, { soundFont: intent.font }), {
        type: 'writeSoundFont',
        font: intent.font,
      });
    // ---- the sheets over the table ----
    case 'rules/open':
      return pure(withShell(app, { rulesOpen: true }));
    case 'rules/close':
      return pure(withShell(app, { rulesOpen: false }));
    case 'history/open':
      return pure(withShell(app, { historyOpen: true }));
    case 'history/close':
      return pure(withShell(app, { historyOpen: false }));
    case 'result/dismiss':
      return pure(withShell(app, { resultDismissed: true }));
    case 'result/open':
      return pure(withShell(app, { resultDismissed: false }));
    case 'pause/continue':
      return pure(withShell(app, { pause: null }));
    case 'again/click':
      return again(app, ctx, cfg);
    case 'escape': {
      // The pause's Continue first: nothing else of the table moves while it is up.
      if (s.pause !== null) return pure(withShell(app, { pause: null }));
      const own = cfg.table.escape?.(app, ctx) ?? null;
      if (own !== null) return own;
      if (s.historyOpen) return pure(withShell(app, { historyOpen: false }));
      return pure(s.rulesOpen ? withShell(app, { rulesOpen: false }) : app);
    }
    case 'flip/set':
      return step(withShell(app, { flipForFar: intent.on }), { type: 'writeFlip', on: intent.on });
    case 'share/click':
      return s.code === null ? pure(app) : step(app, { type: 'share', code: s.code });
    // ---- net: host ----
    case 'host/start':
      return startHost(app, intent.code, ctx, cfg);
    case 'host/status':
      return pure(withHostStatus(app, intent.text, intent.stopPulse));
    case 'host/frame':
      return hostFrame(app, intent.frame, intent.seat ?? 1, ctx, cfg);
    case 'host/guestGone': {
      // The seat's channel is down; its name is kept for the rejoin (the lobby's leave empties it below).
      const seat = intent.seat ?? 1;
      const seats = seatsOf(s, cfg);
      const gone = withSeats(
        app,
        seatAt(seats, seat, { name: seats[seat - 1]?.name ?? null, connected: false }),
      );
      return intent.iceFailed === null
        ? guestGone(gone, seat, ctx, cfg)
        : pure(withHostStatus(gone, intent.iceFailed));
    }
    case 'host/deal': {
      const seated = seatedCount(seatsOf(s, cfg));
      if (seated < minSeated(s, cfg))
        return step(
          app,
          toast(
            cfg.copy.notEnough === undefined
              ? WAITING_FOR_GUEST_MSG
              : cfg.copy.notEnough(seated, minSeated(s, cfg)),
          ),
        );
      // The host, then every guest seat in order (`guest`, `guest2`, `guest3`: seat 1 keeps the
      // two-seat id). A connected seat has a name; the fallback, the word a nameless guest would
      // have chosen for itself, only satisfies the type (a seat left empty at a flexible table is
      // dealt to under it: seats are not compacted, D2).
      const players: ReadonlyArray<Player> = [
        { id: 'host', name: s.myName },
        ...seatsOf(s, cfg).map((seat, i) => ({
          id: i === 0 ? 'guest' : `guest${String(i + 1)}`,
          name: seat.name ?? guestName('', cfg),
        })),
      ];
      const game = cfg.engine.create(playersFor<G>(players), s.opts, ctx.rng, ctx.now);
      // The deal is a tap: the Android lock rides it (`lockSideways`), before the table paints.
      return andThen(lockSideways(app, ctx, cfg), (a) =>
        broadcast(
          { shell: { ...a.shell, game, ...SHEETS_AWAY }, table: resetWith(a.table, 'deal', cfg) },
          ctx,
          cfg,
        ),
      );
    }
    // ---- net: guest ----
    case 'guest/start':
      return startGuest(app, intent.code, cfg);
    case 'guest/status':
      return pure(withGuestStatus(app, intent.text, intent.stopPulse));
    case 'guest/connected':
      return pure(withShell(app, { oppConnected: true }));
    case 'guest/frame':
      return guestFrame(app, intent.frame, ctx, cfg);
    case 'guest/lost': {
      const lost: ShellApp<G> = {
        shell: { ...s, oppConnected: false, ...SHEETS_AWAY },
        table: resetWith(app.table, 'lost', cfg),
      };
      const v = lost.shell.view;
      if (v !== null && !cfg.engine.over(v))
        return andThen(painted(lost, v, ctx, cfg), (a) =>
          step(a, toast(LOST_HOST_MSG, GONE_TOAST_MS)),
        );
      return showScreen(withGuestStatus(lost, DISCONNECTED_MSG), 'guestWaitScreen');
    }
    // ---- the curtain, the leave flow, the paint ----
    case 'curtain/reveal': {
      const game = s.game;
      if (game === null) return pure(app);
      const { seat, effects = [] } = cfg.local.revealer(game);
      // The curtain's Roll is the tap every turn has: the Android lock re-enters on it after a
      // back gesture (`lockSideways`, silent while held), then the reveal.
      return andThen(lockSideways(app, ctx, cfg), (locked) =>
        andThen(
          step(
            {
              shell: { ...locked.shell, revealed: seat },
              table: { ...locked.table, curtain: null },
            },
            tap,
            ...effects,
          ),
          (a) => localBroadcast(a, true, ctx, cfg),
        ),
      );
    }
    case 'position/load':
      return loadPosition(app, intent.state, ctx, cfg);
    case 'leave/request':
      return step(app, {
        type: 'confirm',
        message: s.role === 'local' ? cfg.copy.leaveLocal : cfg.copy.leaveOnline,
        then: { type: 'leave/confirmed' },
      });
    case 'leave/confirmed':
      // The network closes before the reset (see the header), then `leave/finish` resets. The
      // Android lock is dropped with the wake lock (`unlockSideways`: only where it could be held).
      return step(
        app,
        { type: 'wakeLock', hold: false },
        ...unlockSideways(ctx, cfg),
        { type: 'closeNet' },
        { type: 'then', intent: { type: 'leave/finish' } },
      );
    case 'leave/finish':
      return leaveFinish(app, cfg);
    case 'visible':
      return s.role === null ? pure(app) : step(app, { type: 'wakeLock', hold: true });
    // The paint follows (the boot paints after every intent that changes the App): the gate is `gateOpen`'s.
    case 'viewport/portrait': {
      // In a game that plays upright, the turn to upright at a table that came up sideways is where the rotation hint is due.
      const turned = withShell(app, { portraitPhone: intent.portrait });
      return playsOrientation(cfg) === 'portrait' ? rotationHint(turned, ctx, cfg) : pure(turned);
    }
    case 'viewport/landscape':
      // The turn of the phone at a table that came up upright is where the rotation hint is due.
      return rotationHint(withShell(app, { landscapePhone: intent.landscape }), ctx, cfg);
    case 'gate/keep':
      return pure(withShell(app, { gateDismissed: true }));
    case 'gate/turn':
      // "Go sideways": the lock from the gate's own tap; the gate goes when the watcher sees the turn.
      return lockSideways(app, ctx, cfg);
    case 'fullscreen/lost':
      // The lock is gone (a back gesture, a failed attempt): the mark drops, and the rotation hint,
      // silent while it was held, is due now if the table is sideways; nothing when nothing was held.
      return s.orientationLocked
        ? rotationHint(withShell(app, { orientationLocked: false }), ctx, cfg)
        : pure(app);
    case 'render':
      return painted(app, s.view, ctx, cfg);
    case 'persist':
      return step(app, { type: 'persist' });
  }
};

// ---- the initial shell, storage and what the sessions read back ---------------------------------

/** The legacy `app` literal plus the DOM state, on the home screen. */
export const initialShell = <G extends ShellTypes>(cfg: ShellConfig<G>): ShellState<G> => ({
  role: null,
  code: null,
  myName: defaultNameOf(cfg),
  opts: cfg.opts.initial,
  game: null,
  view: null,
  oppName: null,
  oppConnected: false,
  seats: [],
  mySeat: 0,
  seatedName: null,
  localNames: [],
  localSeats: [],
  nameTouched: false,
  revealed: null,
  homeTab: tabsOf(cfg).default,
  playMode: defaultModeOf(cfg),
  p1Name: '',
  p2Name: '',
  seatNames: noSeatNames(cfg),
  screen: 'homeScreen',
  portraitPhone: false,
  landscapePhone: false,
  gateDismissed: false,
  rotationHintShown: false,
  orientationLocked: false,
  netAttempt: 0,
  hostStatus: { text: copyOf(cfg).opening, pulse: true },
  guestStatus: { text: CONNECTING_MSG, pulse: true },
  startGameVisible: false,
  handoff: false,
  savedName: null,
  resume: null,
  openedAt: null,
  rulesOpen: false,
  historyOpen: false,
  resultDismissed: false,
  pause: null,
  cues: initialCuesOf(cfg),
  submenuOpen: false,
  longPressed: false,
  codeDraft: '',
  soundFont: DEFAULT_SOUND_FONT,
  flipForFar: false,
  recentGames: [],
  recorded: null,
});

/** `persist()`: the save for the current role, or null when there is nothing to save. */
export const saveFor = <G extends ShellTypes>(s: ShellState<G>): Save<G> | null => {
  switch (s.role) {
    case 'local':
      return s.game === null ? null : { role: 'local', game: s.game };
    case 'host':
      return {
        role: 'host',
        code: s.code ?? '',
        myName: s.myName,
        ...s.opts,
        game: s.game,
        oppName: s.oppName,
        // The seat names ride only for a room of more than two seats (D6's rejoin key per seat); a two-seat save is the legacy literal.
        ...(s.seats.length > 1 ? { seatNames: s.seats.map((seat) => seat.name) } : {}),
        ...(s.handoff ? { handoff: true } : {}),
        // The stamp rides only on the waiting room (lobby-resume.md D1): a game's save is the legacy literal.
        ...(s.game === null && s.openedAt !== null ? { at: s.openedAt } : {}),
      };
    case 'guest':
      return { role: 'guest', code: s.code ?? '', myName: s.myName };
    case null:
      return null;
  }
};

/** `initHome`'s reads: the names, the tab and mode (defaults when unreadable), the font, the flip, the save, the finished games, and the game's own keys. */
export const readHome = <G extends ShellTypes>(
  store: G['Store'],
  cfg: ShellConfig<G>,
): HomeSnapshot<G> => {
  const name = cfg.prefs.name.read(store);
  const p2Name = cfg.prefs.p2Name.read(store);
  const tab = cfg.prefs.homeTab.read(store);
  const mode = cfg.prefs.playMode.read(store);
  const font = cfg.prefs.soundFont.read(store);
  const flip = cfg.prefs.flipTable.read(store);
  const save = cfg.prefs.save.readSave(store);
  return {
    name: name.ok ? name.value : null,
    p2Name: p2Name.ok ? p2Name.value : null,
    homeTab: tab.ok ? tab.value : tabsOf(cfg).default,
    playMode: mode.ok ? mode.value : defaultModeOf(cfg),
    soundFont: font.ok ? font.value : DEFAULT_SOUND_FONT,
    flipTable: flip.ok && flip.value === 'on',
    save: save.ok ? save.value : null,
    recentGames: cfg.prefs.recentGames.read(store),
    opts: cfg.prefs.opts === undefined ? null : cfg.prefs.opts.read(store),
    // Absent, not null, for a game without the prefs: its snapshot is the literal it was.
    ...(cfg.prefs.seatNames === undefined
      ? {}
      : {
          seatNames: cfg.prefs.seatNames.map((pref) => {
            const name = pref.read(store);
            return name.ok ? name.value : null;
          }),
        }),
    ...homeOf(cfg).read(store),
  };
};

/** What the host session reads back (web/shared/net/host.ts `HostContext<X>`, `X` the room's terms). */
export type HostContextOf<G extends ShellTypes> = Readonly<{
  attempt: number;
  role: Role | null;
  code: string | null;
  myName: string;
  hasGame: boolean;
  handoff: boolean;
  oppName: string | null;
  oppConnected: boolean;
  /** The guest seats as the shell holds them; an N-seat codec lists them in its welcome, a two-seat codec reads none of it. */
  seats: ReadonlyArray<SeatState>;
}> &
  G['Opts'];
export type GuestContextOf = Readonly<{
  attempt: number;
  role: Role | null;
  code: string | null;
  myName: string;
  oppConnected: boolean;
}>;

export const hostContextOf = <G extends ShellTypes>(s: ShellState<G>): HostContextOf<G> => ({
  attempt: s.netAttempt,
  role: s.role,
  code: s.code,
  myName: s.myName,
  ...s.opts,
  hasGame: s.game !== null,
  handoff: s.handoff,
  oppName: s.oppName,
  oppConnected: s.oppConnected,
  seats: s.seats,
});

export const guestContextOf = <G extends ShellTypes>(s: ShellState<G>): GuestContextOf => ({
  attempt: s.netAttempt,
  role: s.role,
  code: s.code,
  myName: s.myName,
  oppConnected: s.oppConnected,
});
